import React, { useState, useEffect, useRef } from "react";
import { ActivityLog } from "../components/ActivityLog";
import { Plus, ChevronRight, X, Star, Eye, Check, XCircle, Briefcase, Mail, Phone, Calendar, Save, AlertTriangle, Trash2, FileText, Users, Clock, PlayCircle, PauseCircle, Edit, Upload, Link as LinkIcon, Copy, RefreshCw, CheckCircle2, UserCheck, Search, Download, RotateCcw } from "lucide-react";
import api, { BASE } from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Cd, Av, Bg, Bt, Tbl, Sl, Inp } from "../components";
import { OfferTemplatesTab, openPreview, PLACEHOLDER_HELP } from "./OfferTemplates";
import { FormModal } from "../components/FormModal";
import { MyInterviewsPage } from "./MyInterviews";
import { notify, confirmDialog } from "../notify";
import { fmtDMY } from "../dateFormat";

// Who shows in the recruiter pickers: anyone whose job role looks like recruiting / HR / talent, plus the people HR Head /
// Admin added on the "Recruiter list" (stored on the server, loaded by RecruitmentPage and shared through this context).
const RecruiterPoolCtx = React.createContext([]);
const isRecruiterCandidate = (em, extraIds = []) => em.st === "Active" && (extraIds.includes(String(em.id)) || /recruit|hr|talent/i.test(em.rl?.t || em.rl?.title || ""));

// Forward funnel stages — Rejected + Not Joined are terminal exits
// shown as a separate pill, not steps in the pipeline. DB values are
// kept legacy ('Offer', not 'Offered') to avoid a data migration;
// STAGE_LABEL maps the DB value to the display label HR asked for.
const STAGES = ["New", "Interviewing", "Offer Initiated", "Offer", "Hired"];
const STAGE_COLORS = [K.t3, K.bl, K.pu, K.a, K.g];
const TERMINAL_EXITS = ["Rejected", "Not Joined", "Withdrawn"];
const STAGE_LABEL = { Offer: "Offered", "Offer Initiated": "Offer To Be Sent", Hired: "Joined" };
const stageLbl = (s) => STAGE_LABEL[s] || s;
const fmtMoney = (v) => v >= 100000 ? "₹" + (v / 100000).toFixed(1) + "L" : "₹" + (v || 0).toLocaleString('en-IN');

// ─── Add Candidate (Phase 2 — links to a Sourcing requisition) ──────────────
// HR opens this from the Candidates tab. It fetches active Sourcing
// requisitions and lets HR pick one — the candidate then inherits the
// requisition's position and hiring manager. Standalone candidates (no
// linked requisition) still work for ad-hoc/legacy entries.
// Source dropdown with "+ Add new source…": the list lives in the database (Recruitment config), so a
// source added here is saved for everyone and shows up in every candidate form from then on.
const SourceSelect = ({ value, sources = [], onChange, onSources, className, blank }) => {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post('/api/recruitment/config/source', { name });
      onSources && onSources(r.sources);
      onChange(r.source);
      setAdding(false); setName("");
      if (r.added) notify(`"${r.source}" added to Source`, { title: 'Saved', variant: 'success' });
    } catch (e) { notify(e.message || 'Could not add source', { title: 'Error', variant: 'error' }); }
    finally { setBusy(false); }
  };
  if (adding) return (
    <div className="flex gap-2">
      <input autoFocus className={className} value={name} maxLength={40} placeholder="New source name"
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } if (e.key === 'Escape') { setAdding(false); setName(""); } }} />
      <button type="button" disabled={busy || !name.trim()} onClick={submit} className="px-3 rounded-lg border-0 bg-pu text-white text-[14px] cursor-pointer disabled:opacity-50">Add</button>
      <button type="button" onClick={() => { setAdding(false); setName(""); }} className="px-3 rounded-lg border border-b0 bg-transparent text-t2 text-[14px] cursor-pointer">Cancel</button>
    </div>
  );
  return (
    <select className={className} value={value} onChange={e => { if (e.target.value === "__add__") setAdding(true); else onChange(e.target.value); }}>
      {blank && <option value="">—</option>}
      {sources.map(s => <option key={s} value={s}>{s}</option>)}
      <option value="__add__">+ Add new source…</option>
    </select>
  );
};

const AddCandidateModal = ({ roles, offices = [], employees = [], myProfile, onClose, onCreated, onViewExisting }) => {
  const extraRecruiterIds = React.useContext(RecruiterPoolCtx);
  // Two-step flow. Step 'intro' asks whether HR has the candidate's CV; if
  // they do, we parse it server-side and skip to the form with fields
  // pre-filled. If they don't, we go straight to a blank form.
  const [step, setStep] = useState('intro'); // 'intro' | 'form'
  const [cvFile, setCvFile] = useState(null);
  const [cvParsing, setCvParsing] = useState(false);
  const [cvExcerpt, setCvExcerpt] = useState('');
  const [prefillNote, setPrefillNote] = useState('');
  const [reqs, setReqs] = useState([]);
  const [reqId, setReqId] = useState("");
  // "Fresher" candidates have no work history or salary yet: Professional Details and Salary Information are switched off.
  const [isFresher, setIsFresher] = useState(false);
  // Admin-editable dropdown lists — sources, notice periods, employee
  // types, qualifications. Falls back to sensible defaults if the fetch
  // fails so the modal is never blocked by a settings hiccup.
  const [rcConfig, setRcConfig] = useState({
    sources: ["Naukri", "LinkedIn", "Shine", "Indeed", "Career page", "Internshala", "Consultant", "Walk in", "Referral"],
    notice_periods: ["Immediate", "15 days", "30 days", "45 days", "60 days", "90 days"],
    employee_types: ["Full", "Contract", "Freelancer", "Internship"],
    qualifications: ["Any Graduate", "Any Postgraduate", "B.TECH", "M.TECH", "MBA", "BBA", "BA", "MA", "B.COM", "M.COM", "B.SC", "M.SC", "PhD", "Diploma", "Other"],
  });
  const [form, setForm] = useState({
    // basic
    name: "", email: "", phone: "", linkedin_url: "",
    gender: "", current_city: "", preferred_location: "",
    // professional
    current_company: "", current_designation: "",
    experience_years: "", relevant_experience_years: "",
    employee_type: "Full",
    // salary
    current_ctc: "", expected_ctc: "",
    current_in_hand: "", expected_in_hand: "", variable_pay: "",
    // notice
    notice_period: "30 days",
    // education
    highest_qualification: "", university: "", passing_year: "", percentage_or_cgpa: "",
    // recruitment
    position: "",
    source: "Naukri",
    recruiter_id: "",
    office_id: myProfile?.employment?.office_id ? String(myProfile.employment.office_id) : "",
    hiring_manager_id: "", rating: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [errField, setErrField] = useState(null);
  const fail = (field, msg) => { setErrField(field); setErr(msg); };
  // Duplicate check — dupMatches null = not yet checked/no matches this
  // attempt; an array = matches found, popup shown, save is paused.
  // dupChecked flips true once the popup's "Continue Anyway" is clicked,
  // so the re-invoked submit() below skips straight to saving instead of
  // re-running the check and re-showing the same popup.
  const [checkingDup, setCheckingDup] = useState(false);
  const [dupMatches, setDupMatches] = useState(null);
  const [dupChecked, setDupChecked] = useState(false);

  const uploadAndParseCv = async () => {
    if (!cvFile) { setErr('Pick a PDF or DOCX first'); return; }
    setErr(null); setCvParsing(true);
    try {
      const fd = new FormData();
      fd.append('cv', cvFile);
      const r = await api.upload('/api/recruitment/parse-cv', fd);
      const f = r?.fields || {};
      const filled = Object.keys(f).filter(k => f[k] != null && f[k] !== '');
      setForm(prev => ({
        ...prev,
        name:                  f.name                  || prev.name,
        email:                 f.email                 || prev.email,
        phone:                 f.phone                 || prev.phone,
        linkedin_url:          f.linkedin_url          || prev.linkedin_url,
        current_designation:   f.current_designation   || prev.current_designation,
        current_company:       f.current_company       || prev.current_company,
        experience_years:      f.experience_years     != null ? String(f.experience_years) : prev.experience_years,
        notice_period:         f.notice_period         || prev.notice_period,
        highest_qualification: f.highest_qualification || prev.highest_qualification,
        university:            f.university            || prev.university,
        passing_year:          f.passing_year         != null ? String(f.passing_year) : prev.passing_year,
        percentage_or_cgpa:    f.percentage_or_cgpa    || prev.percentage_or_cgpa,
      }));
      // The parsed file is NOT stored server-side — parse-cv is extraction
      // only. We keep the File object in `cvFile` and attach it to the
      // candidate right after create, so abandoning this form leaves
      // nothing behind on disk.
      setCvExcerpt(r?.excerpt || '');
      setPrefillNote(filled.length
        ? `Auto-filled ${filled.length} field${filled.length === 1 ? '' : 's'} (${filled.join(', ')}). Please review before saving.`
        : 'Extracted CV text but couldn\'t auto-detect known fields. Fill the form manually.');
      setStep('form');
    } catch (e) {
      setErr(e.message || 'Could not parse CV');
      notify(e.message || 'Could not parse CV', { title: 'Error', variant: 'error' });
    } finally { setCvParsing(false); }
  };

  useEffect(() => {
    api.get("/api/recruitment-requests/sourcing/active")
      .then(r => setReqs(Array.isArray(r) ? r : []))
      .catch(() => setReqs([]));
    api.get("/api/recruitment/config")
      .then(d => d && setRcConfig(prev => ({ ...prev, ...d })))
      .catch(() => {});
  }, []);

  const upd = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errField === k) { setErrField(null); setErr(null); }
  };

  const linkedReq = reqs.find(r => String(r.id) === String(reqId));

  // Auto-fill position from the requisition when it's picked
  useEffect(() => { if (linkedReq && !form.position) upd("position", linkedReq.position); }, [linkedReq?.id]); // eslint-disable-line

  const submit = async () => {
    setErr(null);
    setErrField(null);
    if (!form.name.trim()) return fail('name', "Candidate name is required");
    if (!form.position.trim()) return fail('position', "Applied-for position is required");
    if (!reqId) return fail('reqId', "Pick a hiring requisition — direct hires without a raised request aren't allowed.");
    if (!isFresher) { // freshers have no professional / salary details to fill in
      // Professional Details
      if (!form.current_company.trim()) return fail('current_company', "Current / Last Company is required");
      if (!form.current_designation.trim()) return fail('current_designation', "Current / Last Designation is required");
      if (form.experience_years === "") return fail('experience_years', "Total Experience is required");
      if (form.relevant_experience_years === "") return fail('relevant_experience_years', "Relevant Experience is required");
      // Salary Information
      if (form.current_ctc === "") return fail('current_ctc', "Current CTC is required");
      if (form.current_in_hand === "") return fail('current_in_hand', "Current In-hand is required");
      if (form.variable_pay === "") return fail('variable_pay', "Variable Pay is required");
    }
    // Expected CTC / Expected In-hand stay open (and required) for freshers too.
    if (form.expected_ctc === "") return fail('expected_ctc', "Expected CTC is required");
    if (form.expected_in_hand === "") return fail('expected_in_hand', "Expected In-hand is required");
    // Recruitment Information
    if (!form.source) return fail('source', "Source is required");
    if (!form.recruiter_id) return fail('recruiter_id', "Recruiter Name is required");
    if (linkedReq && !linkedReq.hiring_manager_id) return fail('hiring_manager_id', "The linked requisition has no Hiring Manager set — set one on the requisition before adding candidates against it.");
    if (!linkedReq && !form.hiring_manager_id) return fail('hiring_manager_id', "Hiring Manager is required");

    // Duplicate check — runs once per submit attempt. If the popup shows
    // matches, we stop here instead of saving; "Continue Anyway" sets
    // dupChecked and re-calls submit(), which skips straight past this.
    if (!dupChecked && (form.email.trim() || form.phone.trim())) {
      setCheckingDup(true);
      try {
        const params = new URLSearchParams();
        if (form.email.trim()) params.set('email', form.email.trim());
        if (form.phone.trim()) params.set('phone', form.phone.trim());
        const matches = await api.get(`/api/recruitment/check-duplicate?${params.toString()}`);
        setCheckingDup(false);
        if (Array.isArray(matches) && matches.length > 0) {
          setDupMatches(matches);
          return;
        }
      } catch (e) {
        setCheckingDup(false);
        // A failed duplicate check shouldn't block adding a genuinely new candidate.
      }
    }
    setDupChecked(true);

    setSaving(true);
    try {
      const num = (v) => (v === '' || v == null) ? null : parseFloat(v);
      const created = await api.post("/api/recruitment", {
        ...form,
        rating: num(form.rating),
        experience_years: num(form.experience_years),
        relevant_experience_years: num(form.relevant_experience_years),
        current_ctc: num(form.current_ctc),
        expected_ctc: num(form.expected_ctc),
        current_in_hand: num(form.current_in_hand),
        expected_in_hand: num(form.expected_in_hand),
        variable_pay: num(form.variable_pay),
        passing_year: form.passing_year ? parseInt(form.passing_year) : null,
        request_id: reqId || null,
        office_id: form.office_id ? parseInt(form.office_id) : null,
        hiring_manager_id: form.hiring_manager_id || null,
        recruiter_id: form.recruiter_id || null,
        // Fresher: nothing to record for work history / salary (experience is zero).
        ...(isFresher ? { is_fresher: true, current_company: '', current_designation: '', experience_years: 0, relevant_experience_years: 0,
          current_ctc: null, current_in_hand: null, variable_pay: null } : {}),
      });

      // Attach the CV now that a candidate row exists to own it. Storing it
      // only at this point is what keeps /uploads free of orphans — if HR
      // abandons the form, the file was never written server-side.
      // A failure here is non-fatal: the candidate is already saved, and HR
      // can attach the CV from the profile, so we surface it rather than
      // rolling back a good record.
      if (cvFile && created?.id) {
        try {
          const fd = new FormData();
          fd.append('cv', cvFile);
          await api.upload(`/api/recruitment/${created.id}/resume`, fd);
        } catch (e) {
          setErr(`Candidate saved, but the CV could not be attached (${e.message || 'upload failed'}). Open the candidate and use Upload on the CV tile.`);
          notify(`Candidate saved, but the CV could not be attached (${e.message || 'upload failed'})`, { title: 'Error', variant: 'error' });
          setSaving(false);
          onCreated();
          return;
        }
      }
      notify('Candidate added successfully', { title: 'Saved', variant: 'success' });
      onCreated();
    } catch (e) {
      setErr(e.message || "Save failed");
      notify(e.message || "Save failed", { title: 'Error', variant: 'error' });
    } finally { setSaving(false); }
  };

  const inputCls = "w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none";
  // Red border on the specific invalid field instead of (relying solely
  // on) the top banner — errField is set alongside the message in fail().
  const fieldCls = (key) => "w-full px-3 py-2 bg-bg2 border rounded-[10px] text-[14px] text-t0 outline-none " + (errField === key ? "border-rd" : "border-b0");
  const FieldErr = ({ field }) => errField === field ? <div className="text-[12px] text-r mt-1">{err}</div> : null;

  return <>
  <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[1244px] max-h-[90vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
        <div className="text-[16px] font-extrabold text-t0">Add Candidate</div>
        <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
      </div>
      <div className="p-5">
        {err && !errField && <div className="px-3 py-2 mb-3 rounded-lg text-[14px] font-semibold bg-rb text-r border border-rd">{err}</div>}
        {checkingDup && <div className="px-3 py-2 mb-3 rounded-lg text-[14px] font-semibold bg-bg2 text-t2 border border-b0">Checking for existing candidates…</div>}

        {step === 'intro' && (
          <div>
            <div className="text-center py-6">
              <div className="text-[16px] font-extrabold text-t0 mb-1">Do you have the candidate&rsquo;s CV?</div>
              <div className="text-[14px] text-t3 mb-6 max-w-[420px] mx-auto">
                Upload it and we&rsquo;ll pre-fill name, email, phone, current role and experience so you only fill what&rsquo;s left. If not, jump straight to the form.
              </div>
              <div className="mb-4">
                <label className="inline-flex items-center gap-2 px-4 py-3 bg-bg2 border border-dashed border-b1 rounded-xl cursor-pointer hover:border-p">
                  <Upload size={14} className="text-t2" />
                  <span className="text-[14px] text-t1">{cvFile ? cvFile.name : 'Choose CV — PDF or DOCX'}</span>
                  <input type="file" accept=".pdf,.docx" className="hidden"
                    onChange={e => { setCvFile(e.target.files?.[0] || null); setErr(null); }} />
                </label>
              </div>
              <div className="flex gap-2 justify-center flex-wrap">
                <Bt v="success" icon={Upload} onClick={uploadAndParseCv} disabled={!cvFile || cvParsing}>
                  {cvParsing ? 'Parsing CV…' : 'Yes, upload CV & continue'}
                </Bt>
                <Bt v="secondary" onClick={() => setStep('form')} disabled={cvParsing}>
                  No CV — fill manually
                </Bt>
              </div>
            </div>
          </div>
        )}
        {step === 'form' && (<div>
        {prefillNote && (
          <div className="mb-3 px-3 py-2 rounded-lg text-[14px] font-semibold bg-gb text-g border border-gd">
            {prefillNote}
          </div>
        )}

        {/* Linked requisition picker — mandatory. Standalone candidates
             have been disallowed as a governance guard so every candidate
             rolls up against a raised + approved + allocated hiring
             request. HR/CH can see every hire in flight, no direct-hire
             bypass. */}
        <div className="mb-3 p-3 rounded-lg bg-bg2 border border-b0">
          <div className="text-[14px] text-t3 mb-1">Sourcing for Requisition <span className="text-r">*</span></div>
          <select className={fieldCls('reqId')} value={reqId} onChange={e => { setReqId(e.target.value); if (errField === 'reqId') { setErrField(null); setErr(null); } }} disabled={reqs.length === 0}>
            <option value="">— Select a requisition —</option>
            {reqs.map(r => <option key={r.id} value={r.id}>#{r.id} · {r.position} · {r.department || "—"}{r.hiring_manager_name ? ` · HM: ${r.hiring_manager_name}` : ""} ({r.number_of_openings} opening{r.number_of_openings === 1 ? "" : "s"})</option>)}
          </select>
          <FieldErr field="reqId" />
          {linkedReq && <div className="text-[14px] text-t3 mt-2">
            Inherits HM: <span className="font-semibold text-t1">{linkedReq.hiring_manager_name || "(not set)"}</span> · Department: <span className="font-semibold text-t1">{linkedReq.department || "—"}</span>
          </div>}
          {reqs.length === 0 && (
            <div className="mt-2 p-2 rounded bg-ab border border-ad text-a text-[14px]">
              No requisition is in Sourcing right now. Raise a hiring request, get it CH-approved + HR-allocated, then hit <span className="font-semibold">Start Sourcing</span> on the requisition before adding candidates.
            </div>
          )}
        </div>

        {/* Basic Details */}
        <div className="mt-3">
          <div className="text-[14px] font-bold text-t0 mb-2 uppercase tracking-wide">Basic Details</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <div className="text-[14px] text-t3 mb-1">Candidate Name <span className="text-r">*</span></div>
              <input className={fieldCls('name')} value={form.name} onChange={e => upd("name", e.target.value)} placeholder="Priya Nair" />
              <FieldErr field="name" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Gender</div>
              <select className={inputCls} value={form.gender} onChange={e => upd("gender", e.target.value)}>
                <option value="">Select</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Other</option>
              </select>
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Mobile No</div>
              <input className={inputCls} value={form.phone} onChange={e => upd("phone", e.target.value)} placeholder="+91 98765 43210" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Email</div>
              <input className={inputCls} value={form.email} onChange={e => upd("email", e.target.value)} placeholder="priya@example.com" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">LinkedIn URL</div>
              <input className={inputCls} value={form.linkedin_url} onChange={e => upd("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/…" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Current City</div>
              <input className={inputCls} value={form.current_city} onChange={e => upd("current_city", e.target.value)} placeholder="Bangalore" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Preferred Location</div>
              <input className={inputCls} value={form.preferred_location} onChange={e => upd("preferred_location", e.target.value)} placeholder="Bangalore / Remote" />
            </div>
          </div>
        </div>

        {/* Fresher toggle */}
        <div className="mt-4 flex items-center gap-3 p-3 rounded-lg bg-bg2 border border-b0">
          <button type="button" role="switch" aria-checked={isFresher} onClick={() => setIsFresher(v => !v)}
            className={`relative w-[40px] h-[22px] rounded-full border-0 cursor-pointer transition-colors flex-shrink-0 ${isFresher ? "bg-p" : "bg-b1"}`}>
            <span className={`absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white shadow transition-all ${isFresher ? "left-[20px]" : "left-[2px]"}`} />
          </button>
          <div>
            <div className="text-[14px] font-bold text-t0">Fresher</div>
            <div className="text-[12px] text-t3">Turn on for a fresher — Professional Details and Salary Information are not needed and are switched off.</div>
          </div>
        </div>

        {/* Professional Details */}
        <fieldset disabled={isFresher} className={`mt-4 m-0 p-0 border-0 min-w-0 transition ${isFresher ? "opacity-40 blur-[2px] select-none pointer-events-none" : ""}`}>
          <div className="text-[14px] font-bold text-t0 mb-2 uppercase tracking-wide">Professional Details</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <div className="text-[14px] text-t3 mb-1">Current / Last Company <span className="text-r">*</span></div>
              <input className={fieldCls('current_company')} value={form.current_company} onChange={e => upd("current_company", e.target.value)} placeholder="TCS" />
              <FieldErr field="current_company" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Current / Last Designation <span className="text-r">*</span></div>
              <input className={fieldCls('current_designation')} value={form.current_designation} onChange={e => upd("current_designation", e.target.value)} placeholder="Senior Software Engineer" />
              <FieldErr field="current_designation" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Employee Type</div>
              <select className={inputCls} value={form.employee_type} onChange={e => upd("employee_type", e.target.value)}>
                {rcConfig.employee_types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Total Experience (years) <span className="text-r">*</span></div>
              <input type="number" step="0.5" className={fieldCls('experience_years')} value={form.experience_years} onChange={e => upd("experience_years", e.target.value)} placeholder="5" />
              <FieldErr field="experience_years" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Relevant Experience (years) <span className="text-r">*</span></div>
              <input type="number" step="0.5" className={fieldCls('relevant_experience_years')} value={form.relevant_experience_years} onChange={e => upd("relevant_experience_years", e.target.value)} placeholder="3" />
              <FieldErr field="relevant_experience_years" />
            </div>
          </div>
        </fieldset>

        {/* Salary Information */}
        <div className="mt-4">
          <div className="text-[14px] font-bold text-t0 mb-2 uppercase tracking-wide">Salary Information</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <fieldset disabled={isFresher} className={`m-0 p-0 border-0 min-w-0 transition ${isFresher ? "opacity-40 blur-[2px] select-none pointer-events-none" : ""}`}>
              <div className="text-[14px] text-t3 mb-1">Current CTC (₹) <span className="text-r">*</span></div>
              <input type="number" className={fieldCls('current_ctc')} value={form.current_ctc} onChange={e => upd("current_ctc", e.target.value)} placeholder="1000000" />
              <FieldErr field="current_ctc" />
            </fieldset>
            <div>
              <div className="text-[14px] text-t3 mb-1">Expected CTC (₹) <span className="text-r">*</span></div>
              <input type="number" className={fieldCls('expected_ctc')} value={form.expected_ctc} onChange={e => upd("expected_ctc", e.target.value)} placeholder="1400000" />
              <FieldErr field="expected_ctc" />
            </div>
            <fieldset disabled={isFresher} className={`m-0 p-0 border-0 min-w-0 transition ${isFresher ? "opacity-40 blur-[2px] select-none pointer-events-none" : ""}`}>
              <div className="text-[14px] text-t3 mb-1">Current In-hand (₹) <span className="text-r">*</span></div>
              <input type="number" className={fieldCls('current_in_hand')} value={form.current_in_hand} onChange={e => upd("current_in_hand", e.target.value)} placeholder="700000" />
              <FieldErr field="current_in_hand" />
            </fieldset>
            <div>
              <div className="text-[14px] text-t3 mb-1">Expected In-hand (₹) <span className="text-r">*</span></div>
              <input type="number" className={fieldCls('expected_in_hand')} value={form.expected_in_hand} onChange={e => upd("expected_in_hand", e.target.value)} placeholder="1000000" />
              <FieldErr field="expected_in_hand" />
            </div>
            <fieldset disabled={isFresher} className={`m-0 p-0 border-0 min-w-0 transition ${isFresher ? "opacity-40 blur-[2px] select-none pointer-events-none" : ""}`}>
              <div className="text-[14px] text-t3 mb-1">Variable Pay (₹) <span className="text-r">*</span></div>
              <input type="number" className={fieldCls('variable_pay')} value={form.variable_pay} onChange={e => upd("variable_pay", e.target.value)} placeholder="100000" />
              <FieldErr field="variable_pay" />
            </fieldset>
          </div>
        </div>

        {/* Notice Period */}
        <div className="mt-4 max-w-[320px]">
          <div className="text-[14px] text-t3 mb-1">Notice Period</div>
          <select className={inputCls} value={form.notice_period} onChange={e => upd("notice_period", e.target.value)}>
            {rcConfig.notice_periods.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {/* Education */}
        <div className="mt-4">
          <div className="text-[14px] font-bold text-t0 mb-2 uppercase tracking-wide">Education</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <div className="text-[14px] text-t3 mb-1">Highest Qualification</div>
              <select className={inputCls} value={form.highest_qualification} onChange={e => upd("highest_qualification", e.target.value)}>
                <option value="">Select</option>
                {rcConfig.qualifications.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <div className="text-[14px] text-t3 mb-1">University / College</div>
              <input className={inputCls} value={form.university} onChange={e => upd("university", e.target.value)} placeholder="IIT Bombay" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Passing Year</div>
              <input type="number" min="1980" max="2035" className={inputCls} value={form.passing_year} onChange={e => upd("passing_year", e.target.value)} placeholder="2019" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Percentage / CGPA</div>
              <input className={inputCls} value={form.percentage_or_cgpa} onChange={e => upd("percentage_or_cgpa", e.target.value)} placeholder="8.5 CGPA or 78%" />
            </div>
          </div>
        </div>

        {/* Recruitment Information */}
        <div className="mt-4">
          <div className="text-[14px] font-bold text-t0 mb-2 uppercase tracking-wide">Recruitment Information</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-[14px] text-t3 mb-1">Applied For Position <span className="text-r">*</span></div>
              {linkedReq
                ? <input className={inputCls + " bg-bg2"} value={form.position} readOnly />
                : <select className={fieldCls('position')} value={form.position} onChange={e => upd("position", e.target.value)}>
                    <option value="">Select position</option>
                    {roles.map(r => <option key={r.id || r.t} value={r.t || r.title}>{r.t || r.title}</option>)}
                  </select>
              }
              <FieldErr field="position" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Source <span className="text-r">*</span></div>
              <SourceSelect className={fieldCls('source')} value={form.source} sources={rcConfig.sources} onChange={v => upd("source", v)} onSources={list => setRcConfig(c => ({ ...c, sources: list }))} />
              <FieldErr field="source" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Recruiter Name <span className="text-r">*</span></div>
              <select className={fieldCls('recruiter_id')} value={form.recruiter_id} onChange={e => upd("recruiter_id", e.target.value)}>
                <option value="">Select recruiter</option>
                {employees
                  .filter(em => isRecruiterCandidate(em, extraRecruiterIds))
                  .map(em => <option key={em.id} value={em.id}>{em.nm} — {em.rl?.t || em.rl?.title}</option>)}
              </select>
              <FieldErr field="recruiter_id" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Hiring Location {linkedReq ? "" : <span className="text-r">*</span>}</div>
              {linkedReq ? (
                <input className={inputCls + " bg-bg2"} value={(offices.find(o => o.id === linkedReq.office_id)?.name || linkedReq.office_name || "—")} readOnly />
              ) : (
                <select className={inputCls} value={form.office_id} onChange={e => upd("office_id", e.target.value)}>
                  <option value="">Select office</option>
                  {offices.map(o => <option key={o.id} value={o.id}>{o.name || o.nm}{o.city ? ` — ${o.city}` : ""}</option>)}
                </select>
              )}
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Hiring Manager <span className="text-r">*</span></div>
              {linkedReq ? (
                <input className={inputCls + (linkedReq.hiring_manager_id ? " bg-bg2" : (errField === 'hiring_manager_id' ? " border-rd text-r" : " bg-ab border-ad text-a"))} value={linkedReq.hiring_manager_name || "Not set on this requisition — set one before adding candidates"} readOnly />
              ) : (
                <select className={fieldCls('hiring_manager_id')} value={form.hiring_manager_id} onChange={e => upd("hiring_manager_id", e.target.value)}>
                  <option value="">Select hiring manager</option>
                  {employees.filter(em => em.st === 'Active').map(em => <option key={em.id} value={em.id}>{em.nm}</option>)}
                </select>
              )}
              <FieldErr field="hiring_manager_id" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Initial Rating (1-5)</div>
              <input type="number" step="0.1" min="1" max="5" className={inputCls} value={form.rating} onChange={e => upd("rating", e.target.value)} placeholder="4.0" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-4 pt-3 border-t border-b0">
          <Bt v="secondary" s="sm" onClick={() => setStep('intro')} disabled={saving}>← Back</Bt>
          <Bt v="secondary" s="sm" onClick={onClose} disabled={saving}>Cancel</Bt>
          <Bt v="success" s="sm" icon={Save} onClick={submit} disabled={saving}>{saving ? "Adding…" : "Add Candidate"}</Bt>
        </div>
        </div>)}
      </div>
    </div>
  </div>

  {/* Duplicate-candidate popup — found by email/phone match. Doesn't
       block saving outright; "Continue Anyway" proceeds as a genuinely
       new candidate, "View" jumps straight to the existing one instead
       (closing this whole Add Candidate flow). */}
  {dupMatches && dupMatches.length > 0 && (
    <div className="fixed inset-0 animate-fadeIn bg-black/45 z-[220] flex items-center justify-center p-3" onMouseDown={() => setDupMatches(null)}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[560px] max-h-[85vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.2)]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-b0">
          <div className="flex items-center gap-2 text-[16px] font-extrabold text-t0"><AlertTriangle size={17} color={K.a} />Possible Duplicate Candidate</div>
          <button onClick={() => setDupMatches(null)} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
        </div>
        <div className="p-5">
          <div className="text-[14px] text-t3 mb-4">
            {dupMatches.length === 1 ? "A candidate with this email or phone number already exists:" : `${dupMatches.length} candidates with this email or phone number already exist:`}
          </div>
          <div className="flex flex-col gap-2 mb-4">
            {dupMatches.map(m => (
              <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl border border-b0 bg-bg2">
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-bold text-t0 truncate">{m.name}{m.reopenCount > 0 && <span className="ml-2 text-[11px] font-bold px-[8px] py-[2px] rounded-full" style={{ background: K.pg, color: K.p }}>Revisited</span>}</div>
                  <div className="text-[13px] text-t3 truncate">{m.position || "—"}{m.officeName ? ` · ${m.officeName}` : ""}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <Bg c={TERMINAL_EXITS.includes(m.stage) ? K.r : K.g} sm>{m.stage}</Bg>
                    <span className="text-[12px] text-t3">Applied {fmtDMY(m.createdAt)}</span>
                  </div>
                  {m.rejectionReason && <div className="text-[12px] text-t3 mt-1 italic truncate">"{m.rejectionReason}"</div>}
                </div>
                <Bt v="secondary" s="sm" icon={Eye} onClick={() => onViewExisting?.(m.id)}>View</Bt>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end pt-3 border-t border-b0">
            <Bt v="secondary" s="sm" onClick={() => setDupMatches(null)}>Go Back</Bt>
            <Bt v="warning" s="sm" onClick={() => { setDupMatches(null); setDupChecked(true); submit(); }}>Continue Anyway — Add as New</Bt>
          </div>
        </div>
      </div>
    </div>
  )}
  </>;
};

// ─── Phase 2: Rounds, Documents, Portal Link ────────────────────────────────

const ROUND_OUTCOMES = ["Scheduled", "Pass", "Fail", "Hold"];
const ROUND_OUTCOME_COLOR = { Scheduled: K.bl, Pass: K.g, Fail: K.r, Hold: K.a };
// Display-only labels — the stored value stays Pass/Fail (DB constraint,
// emails, notifications all key off it) but HR wants to see "Selected" /
// "Not Selected" in the UI.
const ROUND_OUTCOME_LABEL = { Scheduled: "Scheduled", Pass: "Selected", Fail: "Not Selected", Hold: "Hold" };
const ROUND_NAMES = ["L1 - Interview", "L2 - Interview", "L3 - Interview", "L4 - Interview"];

const RoundsSection = ({ candId, canEdit, locked = false }) => {
  // Treat the section as effectively read-only once the candidate has been
  // hired/rejected or onboarding has started. canEdit gates HR roles overall;
  // locked fires regardless of role to freeze the record.
  const editable = canEdit && !locked;
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  // Deliberately its own fetch, not the app-wide `employees` list — that
  // list is centre-scoped for non-admin HR roles (getCentreScope), which
  // was silently limiting this dropdown to the requester's own office.
  // Interviewing is cross-centre, so this always asks for everyone.
  const [interviewers, setInterviewers] = useState([]);
  useEffect(() => {
    api.get('/api/recruitment/interviewer-options').then(d => setInterviewers(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  const [newRound, setNewRound] = useState({ name: "", scheduled_at: "", interviewer_id: "", interview_mode: "", virtual_link: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingRound, setSavingRound] = useState(false);
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [reschedulingId, setReschedulingId] = useState(null);
  const [rescheduleValue, setRescheduleValue] = useState("");
  const [savingReschedule, setSavingReschedule] = useState(false);

  const load = () => { setLoading(true); api.get(`/api/recruitment/${candId}/rounds`).then(r => { setRounds(Array.isArray(r) ? r : []); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, [candId]);

  const addRound = async () => {
    if (!newRound.name.trim()) return notify("Round name is required");
    if (newRound.interview_mode === "Virtual") {
      if (!newRound.scheduled_at) return notify("Date & time are required for a virtual interview");
      if (!newRound.virtual_link.trim()) return notify("Meeting link is required for a virtual interview");
    }
    setSavingRound(true);
    try {
      await api.post(`/api/recruitment/${candId}/rounds`, {
        name: newRound.name.trim(),
        // The <input type="datetime-local"> value has no timezone — the
        // browser parses it as local wall-clock time, so convert to a
        // proper UTC instant here. Storing it raw would mean the backend
        // (which treats every stored TIMESTAMP as UTC on read, per
        // server/config/db.js) reinterprets the local time as UTC,
        // shifting the displayed time by the IST offset.
        scheduled_at: newRound.scheduled_at ? new Date(newRound.scheduled_at).toISOString() : null,
        interviewer_id: newRound.interviewer_id || null,
        interview_mode: newRound.interview_mode || null,
        virtual_link: newRound.virtual_link.trim() || null,
      });
      setNewRound({ name: "", scheduled_at: "", interviewer_id: "", interview_mode: "", virtual_link: "" });
      setAdding(false);
      notify("Interview round added", { title: "Saved", variant: "success" });
      load();
    } catch (e) { notify(e.message || "Failed to add round", { title: "Error", variant: "error" }); } finally { setSavingRound(false); }
  };

  const saveOutcome = async (rid, patch) => {
    setSavingOutcome(true);
    try {
      // Empty-string select value must go as null, not "" — the DB check
      // constraint only allows 'Face to Face'/'Virtual' or NULL.
      await api.put(`/api/recruitment/${candId}/rounds/${rid}`, { ...patch, interview_mode: patch.interview_mode || null, virtual_link: patch.virtual_link?.trim() || null });
      setEditingId(null);
      notify("Round outcome saved", { title: "Saved", variant: "success" });
      load();
    } catch (e) { notify(e.message || "Failed to save outcome", { title: "Error", variant: "error" }); } finally { setSavingOutcome(false); }
  };
  const delRound = async (rid) => {
    if (!(await confirmDialog("Delete this interview round?"))) return;
    setDeletingId(rid);
    try {
      await api.del(`/api/recruitment/${candId}/rounds/${rid}`);
      notify("Interview round deleted", { title: "Saved", variant: "success" });
      load();
    } catch (e) { notify(e.message || "Failed to delete round", { title: "Error", variant: "error" }); } finally { setDeletingId(null); }
  };
  const saveReschedule = async (rid) => {
    if (!rescheduleValue) return notify("Pick a new date & time");
    setSavingReschedule(true);
    try {
      await api.put(`/api/recruitment/${candId}/rounds/${rid}`, { scheduled_at: new Date(rescheduleValue).toISOString() });
      setReschedulingId(null);
      setRescheduleValue("");
      notify("Interview rescheduled", { title: "Saved", variant: "success" });
      load();
    } catch (e) { notify(e.message || "Failed to reschedule", { title: "Error", variant: "error" }); } finally { setSavingReschedule(false); }
  };

  const inputCls = "w-full px-2 py-1 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none";
  const toDatetimeLocal = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const openReschedule = (r) => {
    setReschedulingId(r.id);
    setRescheduleValue(r.scheduled_at ? toDatetimeLocal(new Date(r.scheduled_at)) : "");
  };
  const bumpReschedule = (mins) => {
    const base = rescheduleValue ? new Date(rescheduleValue) : new Date();
    base.setMinutes(base.getMinutes() + mins);
    setRescheduleValue(toDatetimeLocal(base));
  };

  return <div className="border-t border-b0 pt-4 mt-4">
    <div className="flex items-center justify-between mb-2">
      <div className="text-[14px] text-t3 uppercase">Interview Rounds</div>
      {editable && !adding && <Bt v="ghost" s="xs" icon={Plus} onClick={() => setAdding(true)}>Add Round</Bt>}
    </div>

    {adding && <div className="bg-bg2 border border-b0 rounded-lg p-3 mb-3">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
        <select className={inputCls} value={newRound.name} onChange={e => setNewRound(f => ({ ...f, name: e.target.value }))}>
          <option value="">Select round</option>
          {ROUND_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <input className={inputCls} type="datetime-local" value={newRound.scheduled_at} onChange={e => setNewRound(f => ({ ...f, scheduled_at: e.target.value }))} />
        <select className={inputCls} value={newRound.interviewer_id} onChange={e => setNewRound(f => ({ ...f, interviewer_id: e.target.value }))}>
          <option value="">Interviewer (optional)</option>
          {interviewers.map(em => <option key={em.id} value={em.employee_id}>{em.name}{em.office_name ? ` (${em.office_name})` : ''}</option>)}
        </select>
        <select className={inputCls} value={newRound.interview_mode} onChange={e => setNewRound(f => ({ ...f, interview_mode: e.target.value }))}>
          <option value="">Interview Mode (optional)</option>
          <option value="Face to Face">Face to Face</option>
          <option value="Virtual">Virtual</option>
        </select>
        {newRound.interview_mode === "Virtual" && (
          <input className={inputCls + " sm:col-span-4"} placeholder="Virtual meeting link (Zoom/Meet/Teams URL)" value={newRound.virtual_link} onChange={e => setNewRound(f => ({ ...f, virtual_link: e.target.value }))} />
        )}
      </div>
      <div className="flex gap-2">
        <Bt v="success" s="xs" onClick={addRound} disabled={savingRound}>{savingRound ? "Adding…" : "Add"}</Bt>
        <Bt v="secondary" s="xs" onClick={() => setAdding(false)} disabled={savingRound}>Cancel</Bt>
      </div>
    </div>}

    {loading ? <div className="text-[14px] text-t3">Loading…</div> :
     rounds.length === 0 ? <div className="text-[14px] text-t3 italic">No rounds yet.</div> :
     <div className="space-y-2">
       {rounds.map(r => {
         const isEdit = editingId === r.id;
         const isReschedule = reschedulingId === r.id;
         return <div key={r.id} className="bg-bg2 border border-b0 rounded-lg p-3">
           <div className="flex items-center gap-2 mb-1 flex-wrap">
             <div className="text-[14px] font-semibold text-t0 flex-1">{r.name}</div>
             <Bg c={ROUND_OUTCOME_COLOR[r.outcome] || K.t3} sm>{ROUND_OUTCOME_LABEL[r.outcome] || r.outcome}</Bg>
             {editable && <>
               {!isReschedule && <button onClick={() => openReschedule(r)} className="text-[14px] text-p bg-transparent border-0 cursor-pointer">Reschedule</button>}
               {!isEdit && <button onClick={() => { setEditingId(r.id); setEditForm({ outcome: r.outcome, feedback: r.feedback || "", interview_mode: r.interview_mode || "", virtual_link: r.virtual_link || "" }); }} className="text-[14px] text-p bg-transparent border-0 cursor-pointer">Edit</button>}
               <button onClick={() => delRound(r.id)} disabled={deletingId === r.id} className="text-[14px] text-r bg-transparent border-0 cursor-pointer">{deletingId === r.id ? "Deleting…" : "Delete"}</button>
             </>}
           </div>
           <div className="text-[14px] text-t3">
             {r.scheduled_at ? `${fmtDMY(r.scheduled_at)}, ${new Date(r.scheduled_at).toLocaleTimeString('en-IN')}` : "Time TBD"}
             {r.interviewer_name && ` · ${r.interviewer_name}`}
             {r.interview_mode && ` · ${r.interview_mode}`}
             {r.interview_mode === "Virtual" && r.virtual_link && <>
               {" · "}<a href={r.virtual_link} target="_blank" rel="noreferrer" className="text-p font-semibold no-underline">Join Link</a>
             </>}
           </div>
           {isReschedule && <div className="mt-2 space-y-2">
             <input className={inputCls} type="datetime-local" value={rescheduleValue} onChange={e => setRescheduleValue(e.target.value)} />
             <div className="flex gap-2 flex-wrap">
               <Bt v="ghost" s="xs" onClick={() => bumpReschedule(30)}>+30 min</Bt>
               <Bt v="ghost" s="xs" onClick={() => bumpReschedule(24 * 60)}>Next day, same time</Bt>
             </div>
             <div className="flex gap-2">
               <Bt v="success" s="xs" onClick={() => saveReschedule(r.id)} disabled={savingReschedule}>{savingReschedule ? "Saving…" : "Save new slot"}</Bt>
               <Bt v="secondary" s="xs" onClick={() => { setReschedulingId(null); setRescheduleValue(""); }} disabled={savingReschedule}>Cancel</Bt>
             </div>
           </div>}
           {!isEdit && !isReschedule && r.feedback && <div className="text-[14px] text-t1 mt-2 whitespace-pre-wrap">{r.feedback}</div>}
           {isEdit && <div className="mt-2 space-y-2">
             <div className="flex gap-2 flex-wrap">
               {ROUND_OUTCOMES.map(o => <button key={o} onClick={() => setEditForm(f => ({ ...f, outcome: o }))} className={`px-2 py-1 rounded text-[14px] font-semibold cursor-pointer border ${editForm.outcome === o ? "" : "opacity-60"}`} style={{ background: ROUND_OUTCOME_COLOR[o] + "14", color: ROUND_OUTCOME_COLOR[o], borderColor: ROUND_OUTCOME_COLOR[o] + "44" }}>{ROUND_OUTCOME_LABEL[o]}</button>)}
             </div>
             <select className={inputCls} value={editForm.interview_mode} onChange={e => setEditForm(f => ({ ...f, interview_mode: e.target.value }))}>
               <option value="">Interview Mode (optional)</option>
               <option value="Face to Face">Face to Face</option>
               <option value="Virtual">Virtual</option>
             </select>
             {editForm.interview_mode === "Virtual" && (
               <input className={inputCls} placeholder="Virtual meeting link (Zoom/Meet/Teams URL)" value={editForm.virtual_link || ""} onChange={e => setEditForm(f => ({ ...f, virtual_link: e.target.value }))} />
             )}
             <textarea className={inputCls + " min-h-[60px] resize-y"} placeholder="Feedback / interview notes" value={editForm.feedback} onChange={e => setEditForm(f => ({ ...f, feedback: e.target.value }))} />
             <div className="flex gap-2">
               <Bt v="success" s="xs" onClick={() => saveOutcome(r.id, editForm)} disabled={savingOutcome}>{savingOutcome ? "Saving…" : "Save"}</Bt>
               <Bt v="secondary" s="xs" onClick={() => setEditingId(null)} disabled={savingOutcome}>Cancel</Bt>
             </div>
           </div>}
         </div>;
       })}
     </div>}
  </div>;
};

const DocumentsSection = ({ candId, canEdit, locked = false }) => {
  const editable = canEdit && !locked;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState("");
  const [busy, setBusy] = useState(null);
  const [addingDoc, setAddingDoc] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const load = () => { setLoading(true); api.get(`/api/recruitment/${candId}/documents`).then(r => { setDocs(Array.isArray(r) ? r : []); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, [candId]);

  const addType = async () => {
    if (!newType.trim()) return;
    setAddingDoc(true);
    try {
      await api.post(`/api/recruitment/${candId}/documents`, { doc_type: newType.trim(), mandatory: false });
      setNewType("");
      setAdding(false);
      notify("Document type added", { title: "Saved", variant: "success" });
      load();
    } catch (e) { notify(e.message || "Failed to add document type", { title: "Error", variant: "error" }); } finally { setAddingDoc(false); }
  };

  const upload = async (docId, file) => {
    if (!file) return;
    setBusy(docId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.upload(`/api/recruitment/${candId}/documents/${docId}/upload`, fd);
      notify("Document uploaded", { title: "Saved", variant: "success" });
      load();
    } catch (e) { notify(e.message || "Upload failed", { title: "Error", variant: "error" }); } finally { setBusy(null); }
  };

  const verify = async (docId, on) => {
    setBusy(docId);
    try {
      await api.put(`/api/recruitment/${candId}/documents/${docId}/verify`, { verified: on });
      notify(on ? "Document verified" : "Document unverified", { title: "Saved", variant: "success" });
      load();
    } catch (e) { notify(e.message || "Failed to update verification", { title: "Error", variant: "error" }); } finally { setBusy(null); }
  };

  const removeDoc = async (docId) => {
    if (!(await confirmDialog("Remove this document slot?"))) return;
    setRemovingId(docId);
    try {
      await api.del(`/api/recruitment/${candId}/documents/${docId}`);
      notify("Document slot removed", { title: "Saved", variant: "success" });
      load();
    } catch (e) { notify(e.message || "Failed to remove document", { title: "Error", variant: "error" }); } finally { setRemovingId(null); }
  };

  return <div className="border-t border-b0 pt-4 mt-4">
    <div className="flex items-center justify-between mb-2">
      <div className="text-[14px] text-t3 uppercase">Documents</div>
      {editable && !adding && <Bt v="ghost" s="xs" icon={Plus} onClick={() => setAdding(true)}>Add Document Type</Bt>}
    </div>
    {adding && <div className="flex gap-2 mb-3">
      <input value={newType} onChange={e => setNewType(e.target.value)} placeholder="e.g. Salary Slip Mar 2026" className="flex-1 px-2 py-1 bg-bg2 border border-b0 rounded text-[14px] outline-none" />
      <Bt v="success" s="xs" onClick={addType} disabled={addingDoc}>{addingDoc ? "Adding…" : "Add"}</Bt>
      <Bt v="secondary" s="xs" onClick={() => { setAdding(false); setNewType(""); }} disabled={addingDoc}>Cancel</Bt>
    </div>}
    {loading ? <div className="text-[14px] text-t3">Loading…</div> :
     docs.length === 0 ? <div className="text-[14px] text-t3 italic">No documents.</div> :
     <div className="space-y-2">
       {docs.map(d => (
         <div key={d.id} className="bg-bg2 border border-b0 rounded-lg p-3 flex items-center gap-3 flex-wrap">
           <FileText size={16} color={d.verified ? K.g : d.file_path ? K.bl : (d.mandatory ? K.a : K.t3)} />
           <div className="flex-1 min-w-0">
             <div className="text-[14px] font-semibold text-t0">{d.doc_type}{d.mandatory && <span className="text-r ml-1">*</span>}</div>
             <div className="text-[12px] text-t3">
               {d.file_path ? <>Uploaded {d.uploaded_at && fmtDMY(d.uploaded_at)}{d.uploaded_by_name ? ` by ${d.uploaded_by_name}` : ""}</> : (d.mandatory ? "Required — not uploaded" : "Optional")}
               {d.verified && d.verified_by_name && <> · Verified by {d.verified_by_name}</>}
             </div>
           </div>
           {d.file_path && <a href={`${BASE}${d.file_path}`} target="_blank" rel="noreferrer" className="text-[14px] text-p font-semibold no-underline">View</a>}
           {editable && <label className="cursor-pointer text-[14px] font-semibold text-p flex items-center gap-1">
             <Upload size={11} />{busy === d.id ? "Uploading…" : (d.file_path ? "Replace" : "Upload")}
             <input type="file" className="hidden" onChange={e => upload(d.id, e.target.files[0])} disabled={busy === d.id} />
           </label>}
           {editable && d.file_path && (d.verified
             ? <button onClick={() => verify(d.id, false)} disabled={busy === d.id} className="text-[14px] text-a bg-transparent border-0 cursor-pointer flex items-center gap-1">{busy === d.id ? "…" : "Unverify"}</button>
             : <button onClick={() => verify(d.id, true)} disabled={busy === d.id} className="text-[14px] text-g font-semibold bg-transparent border-0 cursor-pointer flex items-center gap-1"><CheckCircle2 size={11} />{busy === d.id ? "…" : "Verify"}</button>
           )}
           {editable && !d.mandatory && <button onClick={() => removeDoc(d.id)} disabled={removingId === d.id} className="text-[14px] text-r bg-transparent border-0 cursor-pointer">{removingId === d.id ? "Removing…" : "Remove"}</button>}
         </div>
       ))}
     </div>}
    <div className="text-[12px] text-t3 mt-2">Mandatory documents (*) must be uploaded AND verified before the candidate can be moved to Hired.</div>
  </div>;
};

const PortalLinkSection = ({ candId, accessToken }) => {
  const [token, setToken] = useState(accessToken || "");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => setToken(accessToken || ""), [accessToken]);

  const url = token ? `${window.location.origin}/candidate/${token}` : "";

  const copy = () => { if (!url) return; navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); };
  const regen = async () => {
    if (!(await confirmDialog("Regenerate link? The current URL will stop working."))) return;
    setBusy(true);
    try {
      const r = await api.post(`/api/recruitment/${candId}/regenerate-token`);
      setToken(r.accessToken);
      notify("Portal link regenerated", { title: "Saved", variant: "success" });
    } catch (e) { notify(e.message || "Failed to regenerate link", { title: "Error", variant: "error" }); } finally { setBusy(false); }
  };

  if (!token) return null;
  return <div className="border-t border-b0 pt-4 mt-4">
    <div className="text-[14px] text-t3 uppercase mb-2">Candidate Portal Link</div>
    <div className="flex items-center gap-2 bg-bg2 border border-b0 rounded-lg p-2">
      <LinkIcon size={14} color={K.t3} />
      <input readOnly value={url} className="flex-1 bg-transparent border-0 text-[14px] text-t1 outline-none" onClick={e => e.target.select()} />
      <button onClick={copy} className="text-[14px] font-semibold text-p flex items-center gap-1 bg-transparent border-0 cursor-pointer">
        <Copy size={11} />{copied ? "Copied!" : "Copy"}
      </button>
      <button onClick={regen} disabled={busy} className="text-[14px] font-semibold text-a flex items-center gap-1 bg-transparent border-0 cursor-pointer">
        <RefreshCw size={11} />{busy ? "Regenerating…" : "Regenerate"}
      </button>
    </div>
    <div className="text-[12px] text-t3 mt-1">Share this link with the candidate so they can upload documents and track their status. No login required.</div>
  </div>;
};

// Mirrors PortalLinkSection above, for the separate joining-details form
// link — shown once onboarding has started. The "Accept & Start Onboarding"
// button only fires once and then disappears for good (see the Actions
// block below), so without this, HR had no way to recover if that one
// welcome email bounced, hit spam, or the employee lost it.
const OnboardingLinkSection = ({ candId, onboardingAccessToken }) => {
  const [token, setToken] = useState(onboardingAccessToken || "");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => setToken(onboardingAccessToken || ""), [onboardingAccessToken]);

  const url = token ? `${window.location.origin}/onboarding-form/${token}` : "";

  const copy = () => { if (!url) return; navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); };
  const resend = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/recruitment/${candId}/resend-joining-form`);
      if (r.portalUrl) setToken(r.portalUrl.split('/onboarding-form/')[1]);
      notify(token ? "Joining-form email resent" : "Joining-form email sent", { title: "Sent", variant: "success" });
    } catch (e) { notify(e.message || "Failed to send", { title: "Error", variant: "error" }); } finally { setBusy(false); }
  };

  // Some onboarding records predate this link (created through an older
  // path) and never got a token at all — still show the section so there's
  // a way to generate + send one for the first time, just without a URL to
  // display yet.
  return <div className="border-t border-b0 pt-4 mt-4">
    <div className="text-[14px] text-t3 uppercase mb-2">Onboarding Form Link</div>
    <div className="flex items-center gap-2 bg-bg2 border border-b0 rounded-lg p-2">
      <LinkIcon size={14} color={K.t3} />
      <input readOnly value={url || "Not sent yet"} className="flex-1 bg-transparent border-0 text-[14px] text-t1 outline-none" onClick={e => url && e.target.select()} />
      {url && <button onClick={copy} className="text-[14px] font-semibold text-p flex items-center gap-1 bg-transparent border-0 cursor-pointer">
        <Copy size={11} />{copied ? "Copied!" : "Copy"}
      </button>}
      <button onClick={resend} disabled={busy} className="text-[14px] font-semibold text-a flex items-center gap-1 bg-transparent border-0 cursor-pointer">
        <RefreshCw size={11} />{busy ? "Sending…" : (token ? "Resend Email" : "Send Email")}
      </button>
    </div>
    <div className="text-[12px] text-t3 mt-1">Share this link if the welcome email didn't arrive — it collects personal/family details, PF declarations, and document uploads.</div>
  </div>;
};

const BGV_FIELD_LABELS = [
  ["last_company_name", "Last Company"],
  ["last_company_joining_date", "Joining Date"],
  ["last_working_date", "Last Working Date"],
  ["last_designation", "Last Designation"],
  ["total_experience", "Total Experience"],
  ["last_salary_inhand", "Last Salary — In-hand"],
  ["last_salary_ctc", "Last Salary — CTC"],
  ["expected_salary_inhand", "Expected Salary — In-hand"],
  ["expected_salary_ctc", "Expected Salary — CTC"],
  ["notice_period", "Notice Period"],
  ["current_location", "Current Location"],
  ["reporting_manager_name", "Reporting Manager"],
  ["last_hr_email", "Last Company HR Email"],
  ["last_hr_contact", "Last Company HR Contact"],
  ["reason_for_leaving", "Reason for Leaving"],
  ["uan", "UAN"],
  ["linkedin_id", "LinkedIn"],
];

// BGV (background verification) — HR emails the candidate a link to the
// portal where they fill in last-employment details and upload the 6
// mandatory documents. This gates Interviewing → Offer Initiated (the
// salary-discussion step) server-side.
const BgvSection = ({ candId, email, bgvRequestedAt, bgvSubmittedAt, bgvDetails }) => {
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!email) return notify("This candidate has no email address on file", { title: "Error", variant: "error" });
    if (!(await confirmDialog(`Send the BGV request email to ${email}?`))) return;
    setSending(true);
    try {
      await api.post(`/api/recruitment/${candId}/send-bgv-request`);
      notify("BGV request sent", { title: "Saved", variant: "success" });
    } catch (e) { notify(e.message || "Failed to send BGV request", { title: "Error", variant: "error" }); } finally { setSending(false); }
  };

  return <div className="border-t border-b0 pt-4 mt-4">
    <div className="flex items-center justify-between mb-2">
      <div className="text-[14px] text-t3 uppercase">Background Verification</div>
      {bgvSubmittedAt
        ? <div className="text-[14px] font-semibold text-g flex items-center gap-1"><CheckCircle2 size={12} />Submitted</div>
        : bgvRequestedAt && <div className="text-[14px] font-semibold text-a">Requested — awaiting candidate</div>}
    </div>
    {/* Field grid always shown — blank ("—") until the candidate fills it
        in — so HR can see what's being asked for even before it's sent. */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
      {BGV_FIELD_LABELS.map(([k, label]) => (
        <div key={k} className="bg-bg2 border border-b0 rounded-lg p-2">
          <div className="text-[12px] text-t3 uppercase">{label}</div>
          <div className="text-[14px] text-t0 font-semibold mt-[1px]">{bgvDetails?.[k] || "—"}</div>
        </div>
      ))}
    </div>
    {!bgvSubmittedAt && <Bt s="sm" icon={Mail} onClick={send} disabled={sending}>{sending ? "Sending…" : "Send BGV Request"}</Bt>}
    {!bgvSubmittedAt && <div className="text-[12px] text-t3 mt-2">Emails the candidate a link to submit their last-employment details and required documents. Required before the offer/salary discussion can be initiated.</div>}
  </div>;
};

// Standalone candidates need a retroactive way to attach to a Sourcing
// requisition — otherwise old orphans (created before the mandatory-link
// rule) stay orphans forever. Loads active Sourcing requisitions, HR
// picks one, PUT /:id sets request_id + inherits office/company/HM from
// the requisition server-side.
const LinkRequisitionPanel = ({ c, onLinked }) => {
  const [reqs, setReqs] = useState(null);
  const [pickedId, setPickedId] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get('/api/recruitment-requests/sourcing/active')
      .then(d => setReqs(Array.isArray(d) ? d : []))
      .catch(() => setReqs([]));
  }, []);

  const link = async () => {
    setErr(null);
    if (!pickedId) return setErr('Pick a requisition first');
    setSaving(true);
    try {
      await api.put(`/api/recruitment/${c.id}`, { request_id: parseInt(pickedId) });
      const picked = reqs.find(r => String(r.id) === String(pickedId));
      onLinked?.({
        requestId: parseInt(pickedId),
        requestPosition: picked?.position || null,
        officeId: picked?.office_id || null,
        officeName: picked?.office_name || null,
        hiringManagerId: picked?.hiring_manager_id || null,
        hiringManagerName: picked?.hiring_manager_name || null,
      });
      notify("Candidate linked to requisition", { title: "Saved", variant: "success" });
    } catch (e) {
      setErr(e.message || 'Failed to link');
      notify(e.message || 'Failed to link', { title: 'Error', variant: 'error' });
    }
    finally { setSaving(false); }
  };

  return (
    <div className="mb-4 p-3 rounded-lg bg-ab border border-ad">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle size={12} color={K.a} />
        <div className="text-[14px] font-semibold text-t0">This candidate is not attached to a hiring request</div>
      </div>
      <div className="text-[14px] text-t3 mb-2">
        Every candidate should roll up against a raised + approved + allocated requisition so CH and HR see all hires in flight. Pick the requisition this candidate belongs to — office, company and hiring manager will inherit from it.
      </div>
      {reqs === null ? (
        <div className="text-[14px] text-t3">Loading requisitions…</div>
      ) : reqs.length === 0 ? (
        <div className="text-[14px] text-a">No requisition is in Sourcing right now. Raise one, get it CH-approved + HR-allocated, then hit <span className="font-semibold">Start Sourcing</span> before linking this candidate.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
            <select value={pickedId} onChange={e => setPickedId(e.target.value)}
              className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none">
              <option value="">— Select a requisition —</option>
              {reqs.map(r => <option key={r.id} value={r.id}>#{r.id} · {r.position} · {r.department || '—'}{r.hiring_manager_name ? ` · HM: ${r.hiring_manager_name}` : ''} ({r.number_of_openings} opening{r.number_of_openings === 1 ? '' : 's'})</option>)}
            </select>
            <Bt v="success" s="sm" onClick={link} disabled={saving || !pickedId}>
              {saving ? 'Linking…' : 'Link to Requisition'}
            </Bt>
          </div>
          {err && <div className="text-[14px] text-r mt-2">{err}</div>}
        </>
      )}
    </div>
  );
};

// Candidate detail drawer — all actions (stage moves, reject, offer fields,
// interview notes) live here so the list stays clean.
// Inline editor for Office + Hiring Manager on the candidate detail.
// Only editable for STANDALONE candidates (no linked requisition); when
// linked, Office + HM are inherited from the requisition and shown as
// locked. Backend PUT enforces the same rule defensively.
const CentreHmEditor = ({ c, offices, employees, canEdit, onSaved }) => {
  const linked = !!c.requestId;
  const editable = canEdit && !linked;
  const [editing, setEditing] = useState(false);
  const [officeId, setOfficeId] = useState(c.officeId ? String(c.officeId) : "");
  const [hmId, setHmId] = useState(c.hiringManagerId || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setOfficeId(c.officeId ? String(c.officeId) : "");
    setHmId(c.hiringManagerId || "");
  }, [c.id, c.officeId, c.hiringManagerId]);

  const save = async () => {
    setErr(null);
    if (!officeId) return setErr("Office is required");
    setSaving(true);
    try {
      await api.put(`/api/recruitment/${c.id}`, {
        office_id: parseInt(officeId),
        hiring_manager_id: hmId || null,
      });
      const office = offices.find(o => String(o.id) === officeId);
      const hm = employees.find(e => e.id === hmId);
      onSaved?.({
        officeId: parseInt(officeId),
        officeName: office?.name || office?.nm || null,
        officeCity: office?.city || null,
        hiringManagerId: hmId || null,
        hiringManagerName: hm?.nm || null,
      });
      setEditing(false);
      notify("Centre & hiring manager updated", { title: "Saved", variant: "success" });
    } catch (e) {
      setErr(e.message || "Save failed");
      notify(e.message || "Save failed", { title: "Error", variant: "error" });
    } finally { setSaving(false); }
  };

  const officeLabel = c.officeName ? `${c.officeName}${c.officeCity ? ` — ${c.officeCity}` : ""}` : "—";
  const hmLabel = c.hiringManagerName || "—";
  const isOrphan = !c.officeId && !linked;

  return (
    <div className={`mb-4 p-3 rounded-lg border ${isOrphan ? "bg-ab border-ad" : "bg-bg2 border-b0"}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[14px] text-t3 uppercase">Centre & Hiring Manager</div>
        {editable && !editing && (
          <button onClick={() => setEditing(true)} className="text-[14px] font-semibold text-p bg-transparent border-0 cursor-pointer">
            {isOrphan ? "Assign…" : "Edit"}
          </button>
        )}
      </div>

      {isOrphan && !editing && (
        <div className="text-[14px] text-a mb-2 flex items-center gap-1">
          <AlertTriangle size={11} />Not attributed to a centre — invisible to centre-scoped users until you assign one.
        </div>
      )}

      {!editing ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="bg-bg1 rounded p-2 border border-b0">
            <div className="text-[12px] text-t3 uppercase">Office</div>
            <div className="text-[14px] font-semibold text-t0 mt-[1px]">{officeLabel}</div>
          </div>
          <div className="bg-bg1 rounded p-2 border border-b0">
            <div className="text-[12px] text-t3 uppercase">Hiring Manager</div>
            <div className="text-[14px] font-semibold text-t0 mt-[1px]">{hmLabel}</div>
          </div>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
            <div>
              <div className="text-[12px] text-t3 mb-1">Office <span className="text-r">*</span></div>
              <select value={officeId} onChange={e => setOfficeId(e.target.value)} className="w-full px-2 py-1 bg-bg1 border border-b0 rounded text-[14px] outline-none">
                <option value="">Select office</option>
                {offices.map(o => <option key={o.id} value={o.id}>{o.name || o.nm}{o.city ? ` — ${o.city}` : ""}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[12px] text-t3 mb-1">Hiring Manager</div>
              <select value={hmId} onChange={e => setHmId(e.target.value)} className="w-full px-2 py-1 bg-bg1 border border-b0 rounded text-[14px] outline-none">
                <option value="">— Not assigned —</option>
                {employees.filter(em => em.st === 'Active').map(em => <option key={em.id} value={em.id}>{em.nm}</option>)}
              </select>
            </div>
          </div>
          {err && <div className="text-[14px] text-r mb-2">{err}</div>}
          <div className="flex gap-2 justify-end">
            <Bt v="secondary" s="xs" onClick={() => { setEditing(false); setErr(null); setOfficeId(c.officeId ? String(c.officeId) : ""); setHmId(c.hiringManagerId || ""); }} disabled={saving}>Cancel</Bt>
            <Bt v="success" s="xs" icon={Save} onClick={save} disabled={saving || !officeId}>{saving ? "Saving…" : "Save"}</Bt>
          </div>
        </div>
      )}

      {linked && (
        <div className="text-[12px] text-t3 mt-2">Inherited from Requisition #{c.requestId}. Edit the requisition to change.</div>
      )}
    </div>
  );
};

// Inline edit form for candidate biographical + sourcing fields.
// HR uses this when a phone/email/CTC/notice value needs correcting after
// the candidate is already in the pipeline — no need to reject + re-add.
// Uses the same recruitment_config-driven dropdowns as AddCandidateModal
// so the source / notice / qualification lists stay in sync.
const CandidateEditForm = ({ c, onCancel, onSaved }) => {
  const [form, setForm] = useState({
    name: c.nm || '',
    email: c.email || '',
    phone: c.phone || '',
    gender: c.gender || '',
    position: c.pos || '',
    experience_years: c.exp ?? '',
    current_company: c.curCompany || '',
    current_designation: c.curDesignation || '',
    current_ctc: c.curCtc ?? '',
    expected_ctc: c.expCtc ?? '',
    notice_period: c.notice || '',
    source: c.src || '',
    employee_type: c.empType || '',
    highest_qualification: c.qualification || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [rcConfig, setRcConfig] = useState({
    sources: [], notice_periods: [], employee_types: [], qualifications: [],
  });

  useEffect(() => {
    api.get('/api/recruitment/config').then(setRcConfig).catch(() => {});
  }, []);

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setErr('');
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setSaving(true);
    try {
      // Only send fields the user actually touched (still-original values
      // stay omitted so the backend leaves them alone).
      const patch = {};
      const orig = {
        name: c.nm || '', email: c.email || '', phone: c.phone || '',
        gender: c.gender || '', position: c.pos || '',
        experience_years: c.exp ?? '', current_company: c.curCompany || '',
        current_designation: c.curDesignation || '',
        current_ctc: c.curCtc ?? '', expected_ctc: c.expCtc ?? '',
        notice_period: c.notice || '', source: c.src || '',
        employee_type: c.empType || '', highest_qualification: c.qualification || '',
      };
      for (const k of Object.keys(form)) {
        if (String(form[k] ?? '') !== String(orig[k] ?? '')) patch[k] = form[k] === '' ? null : form[k];
      }
      if (!Object.keys(patch).length) { onCancel(); return; }
      await api.put(`/api/recruitment/${c.id}`, patch);
      // Reflect edits locally — map back to the short-key candidate shape.
      const short = {
        nm: form.name, email: form.email, phone: form.phone, gender: form.gender,
        pos: form.position, exp: form.experience_years === '' ? null : Number(form.experience_years),
        curCompany: form.current_company, curDesignation: form.current_designation,
        curCtc: form.current_ctc === '' ? null : Number(form.current_ctc),
        expCtc: form.expected_ctc === '' ? null : Number(form.expected_ctc),
        notice: form.notice_period, src: form.source,
        empType: form.employee_type, qualification: form.highest_qualification,
      };
      onSaved(short);
      notify('Candidate details updated', { title: 'Saved', variant: 'success' });
    } catch (e) {
      setErr(e.message || 'Save failed');
      notify(e.message || 'Save failed', { title: 'Error', variant: 'error' });
    }
    finally { setSaving(false); }
  };

  const inputCls = "w-full px-3 py-2 bg-bg1 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none";
  const lbl = "text-[12px] text-t3 uppercase mb-1";

  return (
    <div className="mb-4 p-4 rounded-xl bg-bg2 border border-b0">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[14px] font-bold text-t0 flex items-center gap-1"><Edit size={12} />Edit Candidate Details</div>
        <button onClick={onCancel} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={16} /></button>
      </div>
      {err && <div className="mb-3 px-3 py-2 rounded-lg bg-rb text-r border border-rd text-[14px] font-semibold">{err}</div>}

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div><div className={lbl}>Name <span className="text-r">*</span></div><input className={inputCls} value={form.name} onChange={e => upd('name', e.target.value)} /></div>
        <div><div className={lbl}>Position</div><input className={inputCls} value={form.position} onChange={e => upd('position', e.target.value)} placeholder="e.g. Sr Engineer" /></div>
        <div><div className={lbl}>Email</div><input className={inputCls} type="email" value={form.email} onChange={e => upd('email', e.target.value)} /></div>
        <div><div className={lbl}>Phone</div><input className={inputCls} value={form.phone} onChange={e => upd('phone', e.target.value)} /></div>
        <div>
          <div className={lbl}>Gender</div>
          <select className={inputCls} value={form.gender} onChange={e => upd('gender', e.target.value)}>
            <option value="">—</option>
            <option>Male</option><option>Female</option><option>Other</option><option>Prefer not to say</option>
          </select>
        </div>
        <div>
          <div className={lbl}>Employee Type</div>
          <select className={inputCls} value={form.employee_type} onChange={e => upd('employee_type', e.target.value)}>
            <option value="">—</option>
            {(rcConfig.employee_types || []).map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div><div className={lbl}>Experience (yr)</div><input className={inputCls} type="number" step="0.1" min="0" value={form.experience_years} onChange={e => upd('experience_years', e.target.value)} /></div>
        <div><div className={lbl}>Current Company</div><input className={inputCls} value={form.current_company} onChange={e => upd('current_company', e.target.value)} /></div>
        <div><div className={lbl}>Current / Last Designation</div><input className={inputCls} value={form.current_designation} onChange={e => upd('current_designation', e.target.value)} /></div>
        <div>
          <div className={lbl}>Highest Qualification</div>
          <select className={inputCls} value={form.highest_qualification} onChange={e => upd('highest_qualification', e.target.value)}>
            <option value="">—</option>
            {(rcConfig.qualifications || []).map(q => <option key={q}>{q}</option>)}
          </select>
        </div>
        <div><div className={lbl}>Current CTC (₹)</div><input className={inputCls} type="number" min="0" value={form.current_ctc} onChange={e => upd('current_ctc', e.target.value)} placeholder="e.g. 800000" /></div>
        <div><div className={lbl}>Expected CTC (₹)</div><input className={inputCls} type="number" min="0" value={form.expected_ctc} onChange={e => upd('expected_ctc', e.target.value)} placeholder="e.g. 1200000" /></div>
        <div>
          <div className={lbl}>Notice Period</div>
          <select className={inputCls} value={form.notice_period} onChange={e => upd('notice_period', e.target.value)}>
            <option value="">—</option>
            {(rcConfig.notice_periods || []).map(n => <option key={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <div className={lbl}>Source</div>
          <SourceSelect blank className={inputCls} value={form.source} sources={rcConfig.sources || []} onChange={v => upd('source', v)} onSources={list => setRcConfig(c => ({ ...c, sources: list }))} />
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <Bt v="secondary" s="sm" onClick={onCancel} disabled={saving}>Cancel</Bt>
        <Bt v="success" s="sm" icon={Save} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Bt>
      </div>
    </div>
  );
};

export const CandidateDetail = ({ candId, onClose, onRefresh, canEdit, canManagePostSelection = canEdit, canDelete, employees = [], offices = [] }) => {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [rejectMode, setRejectMode] = useState(false);
  const [rejReason, setRejReason] = useState("");
  const [withdrawMode, setWithdrawMode] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [offerCtc, setOfferCtc] = useState("");
  const [offerDate, setOfferDate] = useState("");
  // Offer letter template chosen for Send Offer. Missing fields come back from
  // the server and are asked for in the form under the picker.
  const [offerTemplates, setOfferTemplates] = useState([]);
  const [offerTemplateId, setOfferTemplateId] = useState("");
  const [templateMissing, setTemplateMissing] = useState([]);
  const [templateValues, setTemplateValues] = useState({});
  useEffect(() => {
    api.get("/api/offer-templates").then(d => setOfferTemplates(d?.data || [])).catch(() => {});
  }, []);
  const [joinDate, setJoinDate] = useState("");
  const [expectedJoinDate, setExpectedJoinDate] = useState("");
  const [reschedulingJoin, setReschedulingJoin] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingAction, setSavingAction] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'offer'|'hired'|'notjoined'|'reject'|'delete'
  const [msg, setMsg] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [cvUploading, setCvUploading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // Candidate detail may sit in any bucket — fetch all five so the drawer
    // works whether the user opened it from Active, Hired, Rejected, Not
    // Joined, or Withdrawn tab. No 'all' bucket on the API; ninety-day window
    // is wide enough for the detail lookup but we use a deliberately wide
    // range for terminal buckets.
    const wideFrom = '2000-01-01', wideTo = '2099-12-31';
    Promise.all([
      api.get("/api/recruitment?bucket=active"),
      api.get(`/api/recruitment?bucket=hired&from=${wideFrom}&to=${wideTo}`),
      api.get(`/api/recruitment?bucket=rejected&from=${wideFrom}&to=${wideTo}`),
      api.get(`/api/recruitment?bucket=notjoined&from=${wideFrom}&to=${wideTo}`),
      api.get(`/api/recruitment?bucket=withdrawn&from=${wideFrom}&to=${wideTo}`),
    ]).then(([a, h, rj, nj, wd]) => {
      if (!alive) return;
      const arr = [...(a?.rows || []), ...(h?.rows || []), ...(rj?.rows || []), ...(nj?.rows || []), ...(wd?.rows || [])];
      const found = arr.find(x => x.id === candId);
      if (found) {
        setC(found);
        setNotes(found.notes || "");
        setOfferCtc(found.offerCtc || "");
        setOfferDate(found.offerDate || "");
        setJoinDate(found.joinDate || "");
        setExpectedJoinDate(found.expectedJoinDate || "");
      }
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { alive = false; };
  }, [candId]);

  if (loading || !c) {
    return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-bg1 rounded-2xl p-6 text-t2 text-[15px]">{loading ? "Loading…" : "Candidate not found"}</div>
    </div>;
  }

  const idx = STAGES.indexOf(c.stg);
  const isRejected = c.stg === "Rejected";
  const isNotJoined = c.stg === "Not Joined";
  const isWithdrawn = c.stg === "Withdrawn";
  const isHired = c.stg === "Hired";
  const isOffer = c.stg === "Offer";
  const isOfferInitiated = c.stg === "Offer Initiated";
  const isOnHold = c.stg === "On Hold";
  const isInterviewing = c.stg === "Interviewing";
  const isNew = c.stg === "New";
  // Once a candidate is hired (onboarding started), rejected, marked Not
  // Joined, or withdrawn, the recruitment record is closed: no more rounds,
  // docs, remarks, or stage changes. HR manages hired flows from Onboarding.
  const locked = !!c.onboardingId || isHired || isRejected || isNotJoined || isWithdrawn;

  const flash = (text, kind = "ok") => { setMsg({ text, kind }); setTimeout(() => setMsg(null), 2500); };

  // Attach / replace the candidate's CV. Needed for candidates added
  // before the CV was retained at add time, and whenever HR receives an
  // updated resume.
  const uploadCv = async (file) => {
    if (!file) return;
    setCvUploading(true);
    try {
      const fd = new FormData();
      fd.append('cv', file);
      const r = await api.upload(`/api/recruitment/${c.id}/resume`, fd);
      setC(prev => ({ ...prev, resumePath: r.resume_path }));
      flash(r.replaced ? "CV replaced" : "CV uploaded");
      notify(r.replaced ? "CV replaced" : "CV uploaded", { title: "Saved", variant: "success" });
      onRefresh?.();
    } catch (e) {
      flash(e.message || "CV upload failed", "err");
      notify(e.message || "CV upload failed", { title: "Error", variant: "error" });
    }
    finally { setCvUploading(false); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put("/api/recruitment/" + c.id, { interview_notes: notes });
      // Sync local candidate state so the "no unsaved changes" check on the
      // Save button reflects the new baseline immediately.
      setC(prev => prev ? { ...prev, notes } : prev);
      flash("Remark saved");
      notify("Remark saved", { title: "Saved", variant: "success" });
      onRefresh?.();
    } catch (e) {
      flash(e.message || "Save failed", "err");
      notify(e.message || "Save failed", { title: "Error", variant: "error" });
    } finally { setSavingNotes(false); }
  };

  // Updates the planned/expected join date without touching stage — for
  // when a candidate at Offer needs their anticipated start pushed back.
  // Distinct from Join Date, which finalizes the actual hire date via
  // Mark Joined.
  const reschedule = async () => {
    if (!expectedJoinDate) { flash("Pick a date to reschedule to", "err"); return; }
    setReschedulingJoin(true);
    try {
      await api.put("/api/recruitment/" + c.id, { expected_join_date: expectedJoinDate });
      setC(prev => prev ? { ...prev, expectedJoinDate } : prev);
      flash("Expected join date updated");
      notify("Expected join date updated", { title: "Saved", variant: "success" });
      onRefresh?.();
    } catch (e) {
      flash(e.message || "Reschedule failed", "err");
      notify(e.message || "Reschedule failed", { title: "Error", variant: "error" });
    } finally { setReschedulingJoin(false); }
  };

  const moveStage = async (stage, actionLabel) => {
    setSavingAction(true);
    // "Offer" is a move-target from two different origins (Offer Initiated
    // → Send Offer, and On Hold → Resume) — actionLabel disambiguates the
    // button's loading text; falls back to the stage-based default.
    setPendingAction(actionLabel || (stage === "Offer Initiated" ? "initiate" : stage === "Offer" ? "offer" : stage === "Hired" ? "hired" : stage === "Not Joined" ? "notjoined" : stage === "On Hold" ? "hold" : null));
    try {
      const payload = { stage };
      // Step 1: Initiate Offer — CTC required, offer_initiated_date is
      // auto-stamped server-side (today, app timezone), not sent from here.
      if (stage === "Offer Initiated") {
        if (!offerCtc) { flash("Enter offer CTC before initiating the offer", "err"); setSavingAction(false); setPendingAction(null); return; }
        payload.offer_ctc = parseFloat(offerCtc);
      }
      // Step 2: Send Offer — CTC was already captured at Initiate; this
      // step just records (optionally) the date it was actually sent.
      // (Also reached from On Hold → Offer, the Resume action — no extra
      // payload needed there.)
      if (stage === "Offer" && isOfferInitiated) {
        if (offerDate) payload.offer_date = offerDate;
        if (expectedJoinDate) payload.expected_join_date = expectedJoinDate;
      }
      if (stage === "Hired") {
        if (!joinDate) { flash("Enter join date before marking Hired", "err"); setSavingAction(false); setPendingAction(null); return; }
        payload.join_date = joinDate;
      }
      if (stage === "Offer") {
        if (offerTemplateId) payload.offer_template_id = Number(offerTemplateId);
        const tv = Object.fromEntries(Object.entries(templateValues).filter(([, v]) => v && String(v).trim()));
        if (Object.keys(tv).length) payload.template_values = tv;
      }
      await api.put("/api/recruitment/" + c.id, payload);
      setTemplateMissing([]);
      flash(`Moved to ${stage}`);
      notify(`Candidate moved to ${stageLbl(stage)}`, { title: "Saved", variant: "success" });
      onRefresh?.();
      onClose();
    } catch (e) {
      if (e?.data?.missing) {
        setTemplateMissing(e.data.missing);
        flash("Fill in the highlighted fields to send this letter", "err");
        setSavingAction(false); setPendingAction(null);
        return;
      }
      flash(e.message || "Action failed", "err");
      notify(e.message || "Action failed", { title: "Error", variant: "error" });
    } finally { setSavingAction(false); setPendingAction(null); }
  };

  const acceptOnboarding = async () => {
    setSavingAction(true);
    setPendingAction("accept");
    try {
      const r = await api.post(`/api/recruitment/${c.id}/accept-onboarding`);
      flash(`Onboarding started — ${r.onboardingId}`);
      notify("Onboarding started", { title: "Saved", variant: "success" });
      onRefresh?.();
      onClose();
    } catch (e) {
      flash(e.message || "Action failed", "err");
      notify(e.message || "Action failed", { title: "Error", variant: "error" });
    } finally { setSavingAction(false); setPendingAction(null); }
  };

  const reject = async () => {
    if (!rejReason.trim()) { flash("Reason required", "err"); return; }
    setSavingAction(true);
    setPendingAction("reject");
    try {
      await api.put("/api/recruitment/" + c.id, { stage: "Rejected", rejection_reason: rejReason.trim() });
      flash("Candidate rejected");
      notify("Candidate rejected", { title: "Saved", variant: "success" });
      onRefresh?.();
      onClose();
    } catch (e) {
      flash(e.message || "Reject failed", "err");
      notify(e.message || "Reject failed", { title: "Error", variant: "error" });
    } finally { setSavingAction(false); setPendingAction(null); }
  };

  const withdraw = async () => {
    if (!withdrawReason.trim()) { flash("Reason required", "err"); return; }
    setSavingAction(true);
    setPendingAction("withdraw");
    try {
      await api.put("/api/recruitment/" + c.id, { stage: "Withdrawn", rejection_reason: withdrawReason.trim() });
      flash("Candidate marked as withdrawn");
      notify("Candidate marked as withdrawn", { title: "Saved", variant: "success" });
      onRefresh?.();
      onClose();
    } catch (e) {
      flash(e.message || "Failed to mark withdrawn", "err");
      notify(e.message || "Failed to mark withdrawn", { title: "Error", variant: "error" });
    } finally { setSavingAction(false); setPendingAction(null); }
  };

  const hardDelete = async () => {
    if (!(await confirmDialog(`Permanently delete candidate "${c.nm}"?\n\nThis removes the record from the database and cannot be undone.`))) return;
    setSavingAction(true);
    setPendingAction("delete");
    try {
      await api.del("/api/recruitment/" + c.id);
      flash("Candidate deleted");
      notify("Candidate deleted", { title: "Saved", variant: "success" });
      onRefresh?.();
      onClose();
    } catch (e) {
      flash(e.message || "Delete failed", "err");
      notify(e.message || "Delete failed", { title: "Error", variant: "error" });
    } finally { setSavingAction(false); setPendingAction(null); }
  };

  // Restart a closed-out candidate (Rejected/Withdrawn/Not Joined) instead
  // of them being re-added as a fresh duplicate — resets stage to New and
  // clears the rejection reason, but keeps this drawer open (unlike
  // reject/withdraw/delete above) so HR can immediately re-edit details,
  // re-upload a resume, and add a first interview round. Old rounds/docs
  // are untouched server-side, so that history stays visible right here.
  const reopenCandidate = async () => {
    if (!(await confirmDialog(`Reopen "${c.nm}"? This restarts their interview process from New — their previous ${c.stg.toLowerCase()} history stays on record below.`))) return;
    setSavingAction(true);
    setPendingAction("reopen");
    try {
      await api.post("/api/recruitment/" + c.id + "/reopen", {});
      setC(prev => ({ ...prev, stg: "New", rejReason: null, reopenCount: (prev.reopenCount || 0) + 1 }));
      flash("Candidate reopened — restarted at New");
      notify("Candidate reopened", { title: "Saved", variant: "success" });
      onRefresh?.();
    } catch (e) {
      flash(e.message || "Reopen failed", "err");
      notify(e.message || "Reopen failed", { title: "Error", variant: "error" });
    } finally { setSavingAction(false); setPendingAction(null); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[1047px] max-h-[90vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
          <div className="flex items-center gap-3 min-w-0">
            <Av nm={c.nm} sz={42} />
            <div className="min-w-0">
              <div className="text-[17px] font-extrabold text-t0 truncate flex items-center gap-2">
                {c.nm}
                {c.reopenCount > 0 && <span title={`Reapplied ${c.reopenCount} time${c.reopenCount === 1 ? "" : "s"}`} className="text-[11px] font-bold px-[8px] py-[2px] rounded-full flex-shrink-0" style={{ background: K.pg, color: K.p }}>Revisited</span>}
              </div>
              <div className="flex items-center gap-3 text-[14px] text-t3 mt-[2px]">
                {c.email && <span className="flex items-center gap-1 truncate"><Mail size={10} />{c.email}</span>}
                {c.phone && <span className="flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={20} /></button>
        </div>

        <div className="p-5">
          {/* Flash message */}
          {msg && <div className={`px-3 py-2 mb-3 rounded-lg text-[14px] font-semibold ${msg.kind === "err" ? "bg-rb text-r border border-rd" : "bg-gb text-g border border-gd"}`}>{msg.text}</div>}

          {/* Stage pipeline */}
          <div className="mb-4">
            <div className="text-[14px] text-t3 uppercase mb-2">Current Stage</div>
            <div className="flex gap-1 flex-wrap">
              {STAGES.map((s, i) => {
                const isCurrent = s === c.stg;
                const isPast = idx >= 0 && i < idx;
                return <div key={s} className={`px-[10px] py-1 rounded-full text-[14px] font-semibold ${isCurrent ? "text-white" : isPast ? "text-g" : "text-t3"}`} style={{ background: isCurrent ? STAGE_COLORS[i] : isPast ? K.g + "15" : K.b1 }}>{stageLbl(s)}</div>;
              })}
              {isRejected && <div className="px-[10px] py-1 rounded-full text-[14px] font-semibold text-white" style={{ background: K.r }}>Rejected</div>}
              {isNotJoined && <div className="px-[10px] py-1 rounded-full text-[14px] font-semibold text-white" style={{ background: K.a }}>Not Joined</div>}
              {isOnHold && <div className="px-[10px] py-1 rounded-full text-[14px] font-semibold text-white" style={{ background: K.a }}>On Hold</div>}
            </div>
          </div>

          {/* Info grid — read-only tiles. HR can click Edit Details to
              open the inline form and correct typos or update numbers. */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-[14px] text-t3 uppercase">Candidate Details</div>
            {canEdit && !locked && !editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="text-[14px] font-semibold text-p bg-transparent border-0 cursor-pointer flex items-center gap-1"
              ><Edit size={11} />Edit Details</button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
            {[
              ["Position", c.pos],
              // CV / Resume — opens in a new tab. Renders "No CV" muted
              // when the candidate came in without one (HR fills manually).
              ["CV / Resume", <span className="flex items-center gap-2 flex-wrap">
                {c.resumePath
                  ? <a href={uploadUrl(c.resumePath)} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-p font-semibold no-underline hover:underline">
                      <FileText size={11} /> View CV
                    </a>
                  : <span className="text-t3 italic font-normal">No CV uploaded</span>}
                {!locked && (
                  <label className="inline-flex items-center gap-1 text-[12px] text-p font-semibold cursor-pointer hover:underline">
                    <Upload size={10} />{cvUploading ? "Uploading…" : c.resumePath ? "Replace" : "Upload"}
                    <input type="file" accept=".pdf,.docx" className="hidden" disabled={cvUploading}
                           onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; uploadCv(f); }} />
                  </label>
                )}
              </span>],
              ["Source", c.src || "—"],
              ["Rating", c.rat ? <span className="flex items-center gap-1"><Star size={11} color={K.a} fill={K.a} />{c.rat}</span> : "—"],
              ["Experience", c.exp ? c.exp + " yr" : "—"],
              ["Current Company", c.curCompany || "—"],
              ["Expected CTC", c.expCtc ? fmtMoney(c.expCtc) : "—"],
              ["Current CTC", c.curCtc ? fmtMoney(c.curCtc) : "—"],
              ["Notice Period", c.notice || "—"],
              ["Requisition", c.requestPosition ? `#${c.requestId} · ${c.requestPosition}` : "—"],
            ].map(([l, v]) => <div key={l} className="bg-bg2 rounded-lg p-2 border border-b0">
              <div className="text-[12px] text-t3 uppercase">{l}</div>
              <div className="text-[14px] font-semibold text-t0 mt-[1px]">{v}</div>
            </div>)}
          </div>

          {editMode && canEdit && !locked && (
            <CandidateEditForm
              c={c}
              onCancel={() => setEditMode(false)}
              onSaved={(updated) => {
                setC(prev => prev ? { ...prev, ...updated } : prev);
                setEditMode(false);
                flash('Details updated');
                onRefresh?.();
              }}
            />
          )}

          {/* Link to Hiring Request — only shown for standalone candidates.
              Every new candidate must attach to a Sourcing requisition; this
              panel lets HR retro-link an existing standalone (e.g. Rohit J)
              without deleting + re-adding. */}
          {canEdit && !locked && !c.requestId && (
            <LinkRequisitionPanel c={c} onLinked={(updated) => { setC(prev => ({ ...prev, ...updated })); onRefresh?.(); }} />
          )}

          {/* Centre + Hiring Manager — editable inline for standalone candidates */}
          <CentreHmEditor
            c={c}
            offices={offices}
            employees={employees}
            canEdit={canEdit && !locked}
            onSaved={(updated) => { setC(prev => ({ ...prev, ...updated })); onRefresh?.(); }}
          />

          {/* Rejection reason (read-only) */}
          {isRejected && c.rejReason && <div className="mb-4 p-3 rounded-lg bg-rb border border-rd">
            <div className="text-[12px] text-r uppercase font-semibold mb-1">Rejection Reason</div>
            <div className="text-[14px] text-t0">{c.rejReason}</div>
          </div>}

          {/* Withdrawal reason (read-only) */}
          {isWithdrawn && c.rejReason && <div className="mb-4 p-3 rounded-lg bg-ab border border-ad">
            <div className="text-[12px] text-a uppercase font-semibold mb-1">Withdrawal Reason</div>
            <div className="text-[14px] text-t0">{c.rejReason}</div>
          </div>}

          {/* Onboarding handoff banner — also tells the user the record is now read-only */}
          {c.onboardingId && <div className="mb-4 p-3 rounded-lg bg-gb border border-gd flex items-center gap-2">
            <CheckCircle2 size={16} color={K.g} />
            <div className="flex-1 text-[14px] text-t0">
              <div className="font-semibold text-g">Onboarding started — {c.onboardingId}</div>
              <div className="text-[14px] text-t3">Recruitment is closed for this candidate. Further changes are locked — manage from the Onboarding module.</div>
            </div>
          </div>}

          {/* Rejected lock banner — Reopen restarts the whole pipeline at
               New; existing rounds/docs/reasons stay on record above and
               below, this just unlocks the record for a fresh pass. */}
          {isRejected && <div className="mb-4 p-3 rounded-lg bg-rb border border-rd flex items-center gap-2 flex-wrap">
            <XCircle size={16} color={K.r} />
            <div className="flex-1 text-[14px] text-t0 min-w-[200px]">
              <div className="font-semibold text-r">Candidate Rejected</div>
              <div className="text-[14px] text-t3">This candidate's record is locked. No further changes can be made.</div>
            </div>
            {canManagePostSelection && <Bt v="secondary" s="sm" icon={RotateCcw} onClick={reopenCandidate} disabled={savingAction}>{savingAction && pendingAction === "reopen" ? "Reopening…" : "Reopen"}</Bt>}
          </div>}

          {/* Withdrawn lock banner */}
          {isWithdrawn && <div className="mb-4 p-3 rounded-lg bg-ab border border-ad flex items-center gap-2 flex-wrap">
            <AlertTriangle size={16} color={K.a} />
            <div className="flex-1 text-[14px] text-t0 min-w-[200px]">
              <div className="font-semibold text-a">Candidate Withdrawn</div>
              <div className="text-[14px] text-t3">This candidate's record is locked. No further changes can be made.</div>
            </div>
            {canManagePostSelection && <Bt v="secondary" s="sm" icon={RotateCcw} onClick={reopenCandidate} disabled={savingAction}>{savingAction && pendingAction === "reopen" ? "Reopening…" : "Reopen"}</Bt>}
          </div>}

          {/* Not Joined lock banner — candidate accepted the offer but backed out. */}
          {isNotJoined && <div className="mb-4 p-3 rounded-lg bg-ab border border-ad flex items-center gap-2 flex-wrap">
            <AlertTriangle size={16} color={K.a} />
            <div className="flex-1 text-[14px] text-t0 min-w-[200px]">
              <div className="font-semibold text-a">Candidate Did Not Join</div>
              <div className="text-[14px] text-t3">Offer was extended and accepted, but the candidate did not join. Record is locked.</div>
            </div>
            {canManagePostSelection && <Bt v="secondary" s="sm" icon={RotateCcw} onClick={reopenCandidate} disabled={savingAction}>{savingAction && pendingAction === "reopen" ? "Reopening…" : "Reopen"}</Bt>}
          </div>}

          {/* On Hold banner — reversible pause, not a lock. Only exit is
              Resume back to Offer (see the transition map server-side). */}
          {isOnHold && <div className="mb-4 p-3 rounded-lg bg-ab border border-ad flex items-center gap-2">
            <PauseCircle size={16} color={K.a} />
            <div className="flex-1 text-[14px] text-t0">
              <div className="font-semibold text-a">Offer On Hold</div>
              <div className="text-[14px] text-t3">Paused after the offer was sent. Resume to pick the offer back up.</div>
            </div>
          </div>}

          {/* Actions — semantic transitions only.
              New → Interviewing happens auto on first round add (handled in Rounds card).
              Interviewing → Offer Initiated ("Initiate Offer") needs CTC;
              offer_initiated_date is auto-stamped server-side (today).
              Offer Initiated → Offer ("Send Offer") records when it actually went out.
              Offered → Hired needs join_date AND mandatory docs verified (gated server-side).
              Offered → Not Joined is a terminal exit for candidates who accepted
              but backed out before joining. Offered → On Hold is a reversible
              pause (Resume goes back to Offer) — Reject isn't offered once an
              offer's been sent; On Hold covers that instead. */}
          {canManagePostSelection && !isRejected && !isNotJoined && !isWithdrawn && (isHired ? !c.onboardingId : true) && <div className="border-t border-b0 pt-4">
            <div className="text-[14px] text-t3 uppercase mb-2">Actions</div>

            {isHired && (
              <>
                <div className="text-[14px] text-t3 mb-3">{c.nm} has joined. Accept to create their onboarding record and email them the joining-details form.</div>
                <Bt v="success" icon={ChevronRight} onClick={acceptOnboarding} disabled={savingAction}>{savingAction && pendingAction === "accept" ? "Accepting…" : "Accept & Start Onboarding"}</Bt>
              </>
            )}

            {!isHired && <>
            {isNew && <div className="text-[14px] text-t3 mb-3 italic">Add the first interview round below to move this candidate to Interviewing.</div>}

            {isInterviewing && <div className="mb-3">
              <div className="text-[12px] text-t3 mb-1">Offer CTC (₹) <span className="text-r">*</span></div>
              <input type="number" value={offerCtc} onChange={e => setOfferCtc(e.target.value)} placeholder="1200000" className="w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] outline-none" />
              <div className="text-[12px] text-t3 mt-1">Today's date is recorded automatically as the offer-initiated date.</div>
              {!c.bgvSubmittedAt && <div className="text-[12px] text-a mt-1">Background verification details/documents haven't been submitted yet — send the BGV request below before initiating the offer.</div>}
            </div>}
            {isOfferInitiated && <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[12px] text-t3 mb-1">Offer Date (when it's actually going out)</div>
                <input type="date" value={offerDate} onChange={e => setOfferDate(e.target.value)} className="w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] outline-none" />
              </div>
              <div>
                <div className="text-[12px] text-t3 mb-1">Expected Join Date</div>
                <input type="date" value={expectedJoinDate} onChange={e => setExpectedJoinDate(e.target.value)} className="w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] outline-none" />
                <div className="text-[12px] text-t3 mt-1">Printed on the offer letter — set this before sending or it'll read "TBD".</div>
              </div>
            </div>}
            {isOffer && <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[12px] text-t3 mb-1">Join Date <span className="text-r">*</span></div>
                <input type="date" value={joinDate} onChange={e => setJoinDate(e.target.value)} className="w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] outline-none" />
                <div className="text-[12px] text-t3 mt-1">Mandatory documents must be verified before joining.</div>
              </div>
              <div>
                <div className="text-[12px] text-t3 mb-1">Reschedule Expected Join Date</div>
                <div className="flex gap-1">
                  <input type="date" value={expectedJoinDate} onChange={e => setExpectedJoinDate(e.target.value)} className="flex-1 px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] outline-none" />
                  <Bt v="secondary" s="sm" onClick={reschedule} disabled={reschedulingJoin}>{reschedulingJoin ? "…" : "Reschedule"}</Bt>
                </div>
                <div className="text-[12px] text-t3 mt-1">Updates the planned start date only — doesn't move the stage.</div>
              </div>
            </div>}

            {isOfferInitiated && offerTemplates.length > 0 && (() => {
              const defId = String(offerTemplates.find(t => t.is_default)?.id || "");
              const chosenId = offerTemplateId || defId;
              const chosen = offerTemplates.find(t => String(t.id) === chosenId);
              return (
                <div className="mb-3 p-3 rounded-[10px] bg-bg2 border border-b0">
                  <div className="text-[12px] text-t3 mb-1">Offer letter template</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select value={chosenId} onChange={e => { setOfferTemplateId(e.target.value); setTemplateMissing([]); }} className="flex-1 min-w-[200px] px-3 py-2 bg-bg1 border border-b0 rounded-[10px] text-[14px] outline-none">
                      {offerTemplates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? " (default)" : ""}</option>)}
                    </select>
                    {chosen && <Bt v="secondary" s="sm" icon={Eye} onClick={() => openPreview(chosen.body_html, c.id)}>Preview PDF</Bt>}
                  </div>
                  {templateMissing.length > 0 && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {templateMissing.map(k => (
                        <div key={k}>
                          <div className="text-[12px] text-t3 mb-1">{PLACEHOLDER_HELP[k] || k} <span className="text-r">*</span></div>
                          <input value={templateValues[k] || ""} onChange={e => setTemplateValues(v => ({ ...v, [k]: e.target.value }))} placeholder={k} className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[10px] text-[14px] outline-none" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="flex gap-2 flex-wrap">
              {isInterviewing && <Bt v="success" icon={ChevronRight} onClick={() => moveStage("Offer Initiated")} disabled={savingAction}>{savingAction && pendingAction === "initiate" ? "Initiating…" : "Initiate Offer"}</Bt>}
              {isOfferInitiated && <Bt v="success" icon={ChevronRight} onClick={() => moveStage("Offer")} disabled={savingAction}>{savingAction && pendingAction === "offer" ? "Sending Offer…" : "Send Offer"}</Bt>}
              {isOffer && <Bt v="success" icon={ChevronRight} onClick={() => moveStage("Hired")} disabled={savingAction}>{savingAction && pendingAction === "hired" ? "Marking Joined…" : "Mark Joined"}</Bt>}
              {isOffer && <Bt v="warning" icon={AlertTriangle} onClick={() => moveStage("Not Joined")} disabled={savingAction}>{savingAction && pendingAction === "notjoined" ? "Marking…" : "Mark Not Joined"}</Bt>}
              {isOffer && <Bt v="secondary" icon={PauseCircle} onClick={() => moveStage("On Hold", "hold")} disabled={savingAction}>{savingAction && pendingAction === "hold" ? "Pausing…" : "On Hold"}</Bt>}
              {isOnHold && <Bt v="success" icon={PlayCircle} onClick={() => moveStage("Offer", "resume")} disabled={savingAction}>{savingAction && pendingAction === "resume" ? "Resuming…" : "Resume (back to Offer)"}</Bt>}
              {!isOffer && !isOnHold && !rejectMode && !withdrawMode && <Bt v="danger" icon={XCircle} onClick={() => setRejectMode(true)} disabled={savingAction}>Reject Candidate</Bt>}
              {!rejectMode && !withdrawMode && <Bt v="warning" icon={AlertTriangle} onClick={() => setWithdrawMode(true)} disabled={savingAction}>Mark Withdrawn</Bt>}
            </div>

            {/* Reject form (inline) */}
            {rejectMode && <div className="mt-3 p-3 rounded-lg bg-rb border border-rd">
              <div className="text-[14px] font-semibold text-r mb-2 flex items-center gap-1"><AlertTriangle size={13} />Reject {c.nm}</div>
              <div className="text-[12px] text-t3 mb-1">Rejection Reason <span className="text-r">*</span></div>
              <textarea value={rejReason} onChange={e => setRejReason(e.target.value)} placeholder="Skill gap, culture fit, expected CTC mismatch…" className="w-full min-h-[60px] p-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] outline-none" />
              <div className="flex gap-2 mt-2">
                <Bt v="danger" s="sm" icon={X} onClick={reject} disabled={savingAction || !rejReason.trim()}>{savingAction && pendingAction === "reject" ? "Rejecting…" : "Confirm Reject"}</Bt>
                <Bt v="secondary" s="sm" onClick={() => { setRejectMode(false); setRejReason(""); }} disabled={savingAction}>Cancel</Bt>
              </div>
            </div>}

            {/* Withdraw form (inline) — candidate pulled themselves out,
                distinct from HR-side Reject. */}
            {withdrawMode && <div className="mt-3 p-3 rounded-lg bg-ab border border-ad">
              <div className="text-[14px] font-semibold text-a mb-2 flex items-center gap-1"><AlertTriangle size={13} />Mark {c.nm} as Withdrawn</div>
              <div className="text-[12px] text-t3 mb-1">Withdrawal Reason <span className="text-r">*</span></div>
              <textarea value={withdrawReason} onChange={e => setWithdrawReason(e.target.value)} placeholder="Accepted another offer, personal reasons, unresponsive…" className="w-full min-h-[60px] p-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] outline-none" />
              <div className="flex gap-2 mt-2">
                <Bt v="warning" s="sm" icon={X} onClick={withdraw} disabled={savingAction || !withdrawReason.trim()}>{savingAction && pendingAction === "withdraw" ? "Saving…" : "Confirm Withdrawn"}</Bt>
                <Bt v="secondary" s="sm" onClick={() => { setWithdrawMode(false); setWithdrawReason(""); }} disabled={savingAction}>Cancel</Bt>
              </div>
            </div>}
            </>}
          </div>}

          {/* Interview rounds + Documents (Phase 2) — rounds stay HR/recruiter
              only (sourcing's domain); documents move with the candidate
              into post-selection, so Centre Head manages those too. */}
          <RoundsSection candId={c.id} canEdit={canEdit} locked={locked} />
          <DocumentsSection candId={c.id} canEdit={canManagePostSelection} locked={locked} />

          {/* Remarks / interview notes — placed below Documents so HR captures
              the verdict after reviewing rounds + docs, not before. */}
          <div className="border-t border-b0 pt-4 mt-4">
            <div className="text-[14px] text-t3 uppercase mb-[6px]">Remarks / Interview Notes</div>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={canManagePostSelection && !locked ? "Add remarks, feedback, interview observations…" : "No remarks"}
              disabled={!canManagePostSelection || locked}
              className="w-full min-h-[100px] p-3 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none resize-y"
            />
            {canManagePostSelection && !locked && <div className="flex justify-end mt-2">
              <Bt v="success" s="sm" icon={Save} onClick={saveNotes} disabled={savingNotes || notes === (c.notes || "")}>{savingNotes ? "Saving…" : "Save Remark"}</Bt>
            </div>}
          </div>

          {canManagePostSelection && <BgvSection candId={c.id} email={c.email} bgvRequestedAt={c.bgvRequestedAt} bgvSubmittedAt={c.bgvSubmittedAt} bgvDetails={c.bgvDetails} />}

          {/* Offer details sit above the Candidate Portal Link — only the
              portal link stays pinned at the very bottom. Shown from Offer
              Initiated onward. */}
          {(isOfferInitiated || isOffer || isOnHold || isHired) && <div className="border-t border-b0 pt-4 mt-4">
            <div className="text-[14px] text-t3 uppercase mb-2">Offer Details</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[14px]">
              <div><div className="text-[12px] text-t3">Offer CTC</div><div className="font-semibold">{c.offerCtc ? fmtMoney(c.offerCtc) : "—"}</div></div>
              <div><div className="text-[12px] text-t3">Offer Initiated</div><div className="font-semibold">{c.offerInitiatedDate || "—"}</div></div>
              <div><div className="text-[12px] text-t3">Offer Sent</div><div className="font-semibold">{c.offerDate || "—"}</div></div>
              <div><div className="text-[12px] text-t3">Expected Join</div><div className="font-semibold">{c.expectedJoinDate || "—"}</div></div>
              <div><div className="text-[12px] text-t3">Join Date</div><div className="font-semibold">{c.joinDate || "—"}</div></div>
            </div>
          </div>}

          {canManagePostSelection && <PortalLinkSection candId={c.id} accessToken={c.accessToken} />}
          {canManagePostSelection && c.onboardingId && <OnboardingLinkSection candId={c.id} onboardingAccessToken={c.onboardingAccessToken} />}

          {/* Admin-only permanent delete — available in any state (Rejected, Hired, etc.) */}
          {canDelete && <div className="border-t border-b0 pt-4 mt-4">
            <div className="text-[14px] text-t3 uppercase mb-2">Admin actions</div>
            <Bt v="danger" s="sm" icon={Trash2} onClick={hardDelete} disabled={savingAction}>{savingAction && pendingAction === "delete" ? "Deleting…" : "Permanently Delete Candidate"}</Bt>
            <div className="text-[12px] text-t3 mt-1">Removes the candidate row from the database. Cannot be undone.</div>
          </div>}

          {!canManagePostSelection && <div className="text-[14px] text-t3 text-center py-2 border-t border-b0">View only — actions restricted to Admin / HR Head / Recruiter / Centre Head</div>}
        </div>
      </div>
    </div>
  );
};

// ─── Hiring Requests Tab ────────────────────────────────────────────────────
// Two-stage approval lifecycle:
//   Pending → CH-Approved → Approved → Sourcing → Closed  (Reject branches at Pending or CH-Approved)
// Role responsibilities:
//   · Centre Head  → approves Pending in own centre
//   · HR Head      → approves CH-Approved + allocates recruiter(s)
//   · Recruiter    → sees only requests allocated to them; can start sourcing on Approved
//   · Any raiser   → sees only their own submissions
const REQ_STATUSES = ["Pending", "CH-Approved", "Approved", "On Hold", "Sourcing", "Closed", "Rejected"];
const REQ_STATUS_COLOR = { Pending: K.a, "CH-Approved": K.bl, Approved: K.g, "On Hold": K.a, Sourcing: K.p, Closed: K.t3, Rejected: K.r };
// Priority is the customer-facing label; the DB column is still `urgency`.
// HR asked for a 3-level ladder (Low / Medium / High).
const PRIORITY_COLOR = { Low: K.t3, Medium: K.bl, High: K.r };
const PRIORITIES = ["Low", "Medium", "High"];

const HiringRequestsTab = ({ role, canRaise, isApprover, myProfile, companies = [], departments, offices, employees, initialOpenId, can = () => true }) => {
  const [list, setList] = useState([]);
  const [counts, setCounts] = useState({ active: 0, past: 0 });
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState("active"); // active | past
  // Per-status pill filter inside Active tab (empty = show all in bucket)
  const [statusFilter, setStatusFilter] = useState("");
  // Centre filter: "" = all centres, otherwise the office id (as text) to show only that centre's requests.
  const [centreFilter, setCentreFilter] = useState("");
  const [showRaise, setShowRaise] = useState(false);
  // Opens straight to a specific request's detail view when arriving from
  // a notification link (see requestLink() in recruitment-requests.js) —
  // RequestDetail fetches its own data by id, independent of `list`, so
  // it doesn't matter whether that request is even in the currently
  // loaded bucket/page.
  const [openId, setOpenId] = useState(initialOpenId || null);

  const ninetyDaysAgo = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(ninetyDaysAgo);
  const [to, setTo] = useState(today);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams({ bucket });
    if (bucket === "past") { p.set("from", from); p.set("to", to); }
    api.get(`/api/recruitment-requests?${p.toString()}`)
      .then(d => { setList(d.rows || []); setCounts(d.counts || { active: 0, past: 0 }); setLoading(false); })
      .catch(() => { setList([]); setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bucket, from, to]);

  // Status filter only applies within Active — now includes the CH-Approved stage.
  const ACTIVE_STATUSES = ["Pending", "CH-Approved", "Approved", "Sourcing"];
  // Centres that actually have a request in this list (so the dropdown only offers centres you can see).
  const centreOptions = [...new Map(list.filter(r => r.office_id != null).map(r => [String(r.office_id), r.office_name || ("Centre " + r.office_id)])).entries()]
    .sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  const byCentre = centreFilter ? list.filter(r => String(r.office_id) === centreFilter) : list;
  const filtered = (bucket === "active" && statusFilter)
    ? byCentre.filter(r => r.status === statusFilter)
    : byCentre;
  const activeCounts = ACTIVE_STATUSES.reduce((a, s) => { a[s] = byCentre.filter(r => r.status === s).length; return a; }, {});

  const subTabCls = (k) => `px-[14px] py-[6px] text-[14px] cursor-pointer border-b-2 ${bucket === k ? "font-semibold text-p border-p" : "text-t2 border-transparent"}`;

  return <div>
    {/* Sub-tabs — Active / Past */}
    <div className="flex gap-0 border-b border-b0 mb-3 flex-wrap whitespace-nowrap">
      <button className={subTabCls("active")} onClick={() => { setBucket("active"); setStatusFilter(""); setCentreFilter(""); }}>Active Job ({counts.active})</button>
      <button className={subTabCls("past")}   onClick={() => { setBucket("past"); setStatusFilter(""); setCentreFilter(""); }}>Pause/Closed Job ({counts.past})</button>
    </div>

    {/* Date filter on Past only */}
    {bucket === "past" && (
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-[14px] text-t3 uppercase">Period</span>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="px-2 py-1 bg-bg1 border border-b0 rounded text-[14px] outline-none" />
        <span className="text-[14px] text-t3">to</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} className="px-2 py-1 bg-bg1 border border-b0 rounded text-[14px] outline-none" />
        <button onClick={() => { setFrom(ninetyDaysAgo); setTo(today); }} className="text-[14px] text-p font-semibold bg-transparent border-0 cursor-pointer">Reset to last 90 days</button>
      </div>
    )}

    {/* Status pills — only on Active, only the 3 in-flight states */}
    {bucket === "active" && (
      <Cd style={{ marginBottom: 12 }}>
        <div className="flex gap-[5px] flex-wrap">
          <button onClick={() => setStatusFilter("")} className={`flex-1 min-w-[90px] text-center px-1 py-[10px] rounded-lg border cursor-pointer ${statusFilter === "" ? "ring-2 ring-p" : ""}`} style={{ background: K.b1, borderColor: K.b1 }}>
            <div className="text-[18px] font-extrabold text-t0">{byCentre.length}</div>
            <div className="text-[12px] text-t3 mt-[2px] uppercase">All</div>
          </button>
          {ACTIVE_STATUSES.map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`flex-1 min-w-[90px] text-center px-1 py-[10px] rounded-lg border cursor-pointer ${statusFilter === s ? "ring-2 ring-p" : ""}`} style={{ background: `${REQ_STATUS_COLOR[s]}08`, borderColor: `${REQ_STATUS_COLOR[s]}18` }}>
              <div className="text-[18px] font-extrabold" style={{ color: REQ_STATUS_COLOR[s] }}>{activeCounts[s] || 0}</div>
              <div className="text-[12px] text-t3 mt-[2px] uppercase">{s}</div>
            </button>
          ))}
        </div>
      </Cd>
    )}

    {/* Action bar */}
    <div className="flex justify-between items-center mb-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-[14px] text-t3">{filtered.length} request{filtered.length === 1 ? "" : "s"}{statusFilter ? ` · ${statusFilter}` : ""}{centreFilter ? ` · ${(centreOptions.find(([id]) => id === centreFilter) || [])[1] || "centre"}` : ""}</div>
        {centreOptions.length > 0 && <div className="w-[210px]"><Sl options={[{ v: "", l: "All centres" }, ...centreOptions.map(([id, nm]) => ({ v: id, l: nm }))]} value={centreFilter} onChange={setCentreFilter} /></div>}
      </div>
      {canRaise && bucket === "active" && <Bt icon={Plus} s="sm" onClick={() => setShowRaise(true)}>Raise Hiring Request</Bt>}
    </div>

    {/* List */}
    {loading ? (
      <div className="text-center p-[30px] text-t3 text-[14px]">Loading…</div>
    ) : filtered.length === 0 ? (
      <div className="text-center p-[30px] text-t3 text-[14px]">
        {bucket === "active" ? "No active hiring requests" : "No closed/rejected requests in this period"}
      </div>
    ) : (
      <Tbl cols={[
        { k: "pos", l: "Position", r: r => <div>
          <div className="text-[14px] font-semibold text-t0">{r.position}</div>
          <div className="text-[12px] text-t3">{r.department || "—"}{r.office_name ? ` · ${r.office_name}` : ""}</div>
        </div> },
        { k: "open", l: "Openings", r: r => <span className="text-[15px] font-bold text-t0">{r.number_of_openings}</span> },
        { k: "urg", l: "Priority", r: r => <Bg c={PRIORITY_COLOR[r.urgency] || K.t3} sm>{r.urgency}</Bg> },
        { k: "by", l: "Raised By", r: r => <div>
          <div className="text-[14px] text-t0">{r.raised_by_name || r.raised_by}</div>
          <div className="text-[12px] text-t3">{r.created_at ? fmtDMY(r.created_at) : ""}</div>
        </div> },
        { k: "st", l: "Status", r: r => <Bg c={REQ_STATUS_COLOR[r.status] || K.t3} sm>{r.status}</Bg> },
        { k: "extra", l: bucket === "past" ? "Closed" : "",
          r: r => bucket === "past"
            ? <span className="text-[12px] text-t2">{r.closed_at ? fmtDMY(r.closed_at) : "—"}{r.closed_reason ? ` · ${r.closed_reason}` : r.rejection_reason ? ` · ${r.rejection_reason}` : ""}</span>
            : <span className="text-t3">—</span>
        },
        { k: "act", l: "", r: r => <Bt v="ghost" s="xs" icon={Eye} onClick={() => setOpenId(r.id)}>View</Bt> },
      ]} data={filtered} />
    )}

    {showRaise && <RaiseRequestModal companies={companies} departments={departments} offices={offices} employees={employees} onClose={() => setShowRaise(false)} onCreated={() => { setShowRaise(false); load(); }} />}
    {openId && <RequestDetail id={openId} role={role} myProfile={myProfile} employees={employees} offices={offices} onClose={() => setOpenId(null)} onRefresh={load} can={can} />}
  </div>;
};

// Default qualification list — overridden by /api/recruitment/config
// on mount so admin edits in Settings show up here immediately.
const DEFAULT_QUALIFICATIONS = ["Any Graduate", "Any Postgraduate", "B.TECH", "M.TECH", "MBA", "BBA", "BA", "MA", "B.COM", "M.COM", "B.SC", "M.SC", "PhD", "Diploma", "Other"];

const RaiseRequestModal = ({ companies = [], departments, offices, employees = [], onClose, onCreated }) => {
  const [form, setForm] = useState({
    position: "", company_id: "", department_id: "", office_id: "", number_of_openings: 1,
    urgency: "Medium", justification: "", skills_required: "",
    budget_min: "", budget_max: "", expected_join_date: "", hiring_manager_id: "",
    min_experience_years: "", min_qualification: "", test_required: false,
  });
  const [qualifications, setQualifications] = useState(DEFAULT_QUALIFICATIONS);
  useEffect(() => { api.get('/api/recruitment/config').then(d => d?.qualifications && setQualifications(d.qualifications)).catch(() => {}); }, []);
  const [jdFile, setJdFile] = useState(null);
  const [testFile, setTestFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null);
    if (!form.position.trim()) return setErr("Position is required");
    if (!form.department_id) return setErr("Department is required");
    if (!form.justification.trim()) return setErr("Justification is required");
    if (jdFile && jdFile.size > 5 * 1024 * 1024) return setErr("JD file too large (max 5MB)");
    if (form.test_required && testFile && testFile.size > 5 * 1024 * 1024) return setErr("Test file too large (max 5MB)");
    setSaving(true);
    try {
      const created = await api.post("/api/recruitment-requests", {
        ...form,
        number_of_openings: parseInt(form.number_of_openings) || 1,
        budget_min: form.budget_min ? parseFloat(form.budget_min) : null,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null,
        min_experience_years: form.min_experience_years !== "" ? parseFloat(form.min_experience_years) : null,
        min_qualification: form.min_qualification || null,
        test_required: !!form.test_required,
      });
      // Upload JD as a follow-up call so a JD failure doesn't roll back the request
      if (jdFile && created?.id) {
        const fd = new FormData();
        fd.append("file", jdFile);
        try { await api.upload(`/api/recruitment-requests/${created.id}/jd`, fd); }
        catch (jdErr) {
          console.warn("JD upload failed, request was still saved", jdErr);
          notify(`Request saved, but the JD could not be attached (${jdErr.message || "upload failed"})`, { title: "Error", variant: "error" });
        }
      }
      // Test paper — only meaningful when test_required is set.
      if (form.test_required && testFile && created?.id) {
        const fd = new FormData();
        fd.append("file", testFile);
        try { await api.upload(`/api/recruitment-requests/${created.id}/test`, fd); }
        catch (tErr) {
          console.warn("Test upload failed, request was still saved", tErr);
          notify(`Request saved, but the test paper could not be attached (${tErr.message || "upload failed"})`, { title: "Error", variant: "error" });
        }
      }
      notify("Hiring request submitted", { title: "Saved", variant: "success" });
      onCreated();
    } catch (e) {
      setErr(e.message || "Save failed");
      notify(e.message || "Save failed", { title: "Error", variant: "error" });
    } finally { setSaving(false); }
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = "w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none";

  return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[936px] max-h-[90vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
        <div className="text-[16px] font-extrabold text-t0">Raise Hiring Request</div>
        <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
      </div>
      <div className="p-5">
        {err && <div className="px-3 py-2 mb-3 rounded-lg text-[14px] font-semibold bg-rb text-r border border-rd">{err}</div>}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <div className="text-[14px] text-t3 mb-1">Position <span className="text-r">*</span></div>
            <input className={inputCls} value={form.position} onChange={e => upd("position", e.target.value)} placeholder="Senior Frontend Engineer" />
          </div>
          <div>
            <div className="text-[14px] text-t3 mb-1">Company</div>
            <select className={inputCls} value={form.company_id} onChange={e => upd("company_id", e.target.value)}>
              <option value="">Auto (raiser's company)</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[14px] text-t3 mb-1">Department <span className="text-r">*</span></div>
            <select className={inputCls} value={form.department_id} onChange={e => upd("department_id", e.target.value)}>
              <option value="">Select department</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[14px] text-t3 mb-1">Office</div>
            <select className={inputCls} value={form.office_id} onChange={e => upd("office_id", e.target.value)}>
              <option value="">Any / Not specified</option>
              {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[14px] text-t3 mb-1">Number of Openings</div>
            <input type="number" min="1" className={inputCls} value={form.number_of_openings} onChange={e => upd("number_of_openings", e.target.value)} />
          </div>
          <div>
            <div className="text-[14px] text-t3 mb-1">Priority</div>
            <select className={inputCls} value={form.urgency} onChange={e => upd("urgency", e.target.value)}>
              {PRIORITIES.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[14px] text-t3 mb-1">Budget Min (₹/yr)</div>
            <input type="number" className={inputCls} value={form.budget_min} onChange={e => upd("budget_min", e.target.value)} placeholder="800000" />
          </div>
          <div>
            <div className="text-[14px] text-t3 mb-1">Budget Max (₹/yr)</div>
            <input type="number" className={inputCls} value={form.budget_max} onChange={e => upd("budget_max", e.target.value)} placeholder="1500000" />
          </div>
          <div>
            <div className="text-[14px] text-t3 mb-1">Minimum Experience (years)</div>
            <input type="number" step="0.5" min="0" className={inputCls} value={form.min_experience_years} onChange={e => upd("min_experience_years", e.target.value)} placeholder="e.g. 3" />
          </div>
          <div>
            <div className="text-[14px] text-t3 mb-1">Minimum Qualification</div>
            <select className={inputCls} value={form.min_qualification} onChange={e => upd("min_qualification", e.target.value)}>
              <option value="">Not specified</option>
              {qualifications.map(q => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[14px] text-t3 mb-1">Expected Join Date</div>
            <input type="date" className={inputCls} value={form.expected_join_date} onChange={e => upd("expected_join_date", e.target.value)} />
          </div>
          <div>
            <div className="text-[14px] text-t3 mb-1">Hiring Manager</div>
            <select className={inputCls} value={form.hiring_manager_id} onChange={e => upd("hiring_manager_id", e.target.value)}>
              <option value="">Pick later (required to start sourcing)</option>
              {employees.filter(em => em.st === 'Active').map(em => <option key={em.id} value={em.id}>{em.nm}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <div className="text-[14px] text-t3 mb-1">Skills Required</div>
            <textarea className={inputCls + " min-h-[60px] resize-y"} value={form.skills_required} onChange={e => upd("skills_required", e.target.value)} placeholder="React, TypeScript, design systems…" />
          </div>
          <div className="col-span-2">
            <div className="text-[14px] text-t3 mb-1">Justification <span className="text-r">*</span></div>
            <textarea className={inputCls + " min-h-[80px] resize-y"} value={form.justification} onChange={e => upd("justification", e.target.value)} placeholder="Why is this role needed? Backfill / new project / scaling team…" />
          </div>
          <div className="col-span-2">
            <div className="text-[14px] text-t3 mb-1">Job Description (PDF / DOCX, max 5MB)</div>
            <div className="flex items-center gap-2">
              <label className="cursor-pointer flex items-center gap-1 px-3 py-2 rounded-[10px] text-[14px] font-semibold bg-bg2 border border-b0 hover:border-p">
                <Upload size={12} />
                {jdFile ? "Change file" : "Choose file"}
                <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={e => setJdFile(e.target.files[0] || null)} />
              </label>
              {jdFile && <div className="flex items-center gap-2 flex-1 min-w-0">
                <FileText size={14} color={K.p} />
                <span className="text-[14px] text-t1 truncate">{jdFile.name}</span>
                <button onClick={() => setJdFile(null)} className="text-[14px] text-r bg-transparent border-0 cursor-pointer">Remove</button>
              </div>}
            </div>
          </div>
          <div className="col-span-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.test_required} onChange={e => { upd("test_required", e.target.checked); if (!e.target.checked) setTestFile(null); }} />
              <span className="text-[14px] text-t0 font-semibold">Assessment / Test required</span>
              <span className="text-[14px] text-t3">HR will conduct a test as part of the interview process</span>
            </label>
            {form.test_required && (
              <div className="mt-2 pl-6">
                <div className="text-[14px] text-t3 mb-1">Test Paper (PDF / DOCX, max 5MB)</div>
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer flex items-center gap-1 px-3 py-2 rounded-[10px] text-[14px] font-semibold bg-bg2 border border-b0 hover:border-p">
                    <Upload size={12} />
                    {testFile ? "Change file" : "Choose file"}
                    <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => setTestFile(e.target.files[0] || null)} />
                  </label>
                  {testFile && <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText size={14} color={K.p} />
                    <span className="text-[14px] text-t1 truncate">{testFile.name}</span>
                    <button onClick={() => setTestFile(null)} className="text-[14px] text-r bg-transparent border-0 cursor-pointer">Remove</button>
                  </div>}
                </div>
                <div className="text-[12px] text-t3 mt-1">Optional — you can also upload the test paper later from the request detail.</div>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-4 pt-3 border-t border-b0">
          <Bt v="secondary" s="sm" onClick={onClose} disabled={saving}>Cancel</Bt>
          <Bt v="success" s="sm" icon={Save} onClick={submit} disabled={saving}>{saving ? "Submitting…" : "Submit Request"}</Bt>
        </div>
      </div>
    </div>
  </div>;
};

const RequestDetail = ({ id, role, myProfile, employees = [], offices = [], onClose, onRefresh, can = () => true }) => {
  const extraRecruiterIds = React.useContext(RecruiterPoolCtx);
  // Role-band flags for stage-appropriate action gating.
  const canCH = can('recruitment', 'requests', 'ch_approve', 'edit');
  const canHR = can('recruitment', 'requests', 'hr_approve', 'edit');
  const canManageRecruiters = can('recruitment', 'requests', 'manage_recruiters', 'edit');
  const myEmpId = myProfile?.employee_id;
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejReason, setRejReason] = useState("");
  const [closeMode, setCloseMode] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [reopenMode, setReopenMode] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [reopenOpenings, setReopenOpenings] = useState("");
  const [sourcingMode, setSourcingMode] = useState(false);
  const [pickedHm, setPickedHm] = useState("");
  // Allocation UI — HR Head picks 1+ recruiters when approving.
  const [allocateMode, setAllocateMode] = useState(false);
  const [pickedRecruiters, setPickedRecruiters] = useState([]);
  // Add / change recruiters after approval (Approved / Sourcing / On Hold).
  const [recMode, setRecMode] = useState(false);
  // Edit UI — raiser or HR Head can revise before recruiters are allocated.
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  // Priority quick-pick from the header pill — works on any pre-terminal
  // stage so HR can bump priority without opening the full editor.
  const [priorityMenuOpen, setPriorityMenuOpen] = useState(false);
  // Candidates attached to this requisition — count + mini-list.
  const [cands, setCands] = useState(null);
  // View a candidate profile from the candidates mini-list without
  // leaving the requisition drawer.
  const [openCand, setOpenCand] = useState(null);
  const [msg, setMsg] = useState(null);
  // Re-upload / first-upload for the JD and test-paper attachments — the
  // POST endpoints already replace whatever file was there before, this
  // was just never wired up to a button after the request was created.
  const [jdUploading, setJdUploading] = useState(false);
  const [testUploading, setTestUploading] = useState(false);
  const jdInputRef = useRef(null);
  const testInputRef = useRef(null);

  const loadCands = () => {
    api.get(`/api/recruitment-requests/${id}/candidates`)
      .then(d => setCands(d))
      .catch(() => setCands({ rows: [], counts: { total: 0 }, hired: 0, openings: 0, remaining: 0 }));
  };
  useEffect(() => { loadCands(); /* eslint-disable-next-line */ }, [id]);

  const load = () => {
    setLoading(true);
    // Detail might live in either Active or Past — fetch both buckets so the
    // drawer works regardless of which tab the user is viewing. Cheap on
    // the wire (max 500 each), and the row count is small in practice.
    Promise.all([
      api.get("/api/recruitment-requests?bucket=active"),
      api.get("/api/recruitment-requests?bucket=past"),
    ]).then(([a, p]) => {
      const arr = [...(a?.rows || []), ...(p?.rows || [])];
      const found = arr.find(x => x.id === id);
      setR(found || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(load, [id]);

  const uploadAttachment = async (file, kind) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return notify(`File too large (max 5MB)`, { title: "Error", variant: "error" });
    const setUploading = kind === "jd" ? setJdUploading : setTestUploading;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.upload(`/api/recruitment-requests/${r.id}/${kind}`, fd);
      notify(`${kind === "jd" ? "JD" : "Test paper"} uploaded`, { title: "Saved", variant: "success" });
      load();
    } catch (e) {
      notify(e.message || "Upload failed", { title: "Error", variant: "error" });
    } finally { setUploading(false); }
  };

  const [busyTag, setBusyTag] = useState(null);
  const ACT_SUCCESS_MSG = {
    "ch-approve": "Request approved by Centre Head",
    approve: "Request approved and recruiters allocated",
    reject: "Request rejected",
    "start-sourcing": "Sourcing started",
    "on-hold": "Request put on hold",
    resume: "Request resumed",
    close: "Request closed",
    reopen: "Request reopened",
    edit: "Request updated",
    priority: "Priority updated",
    recruiters: "Recruiters updated",
  };
  const flash = (text, kind = "ok") => { setMsg({ text, kind }); setTimeout(() => setMsg(null), 2500); };
  const act = async (path, body, tag) => {
    setBusy(true);
    setBusyTag(tag || null);
    try {
      await api.put(`/api/recruitment-requests/${id}${path}`, body || {});
      flash("Done");
      notify(ACT_SUCCESS_MSG[tag] || "Saved", { title: "Saved", variant: "success" });
      onRefresh?.();
      load();
      setRejectMode(false); setCloseMode(false); setSourcingMode(false); setAllocateMode(false); setRecMode(false); setEditMode(false); setReopenMode(false); setRejReason(""); setCloseReason(""); setReopenReason(""); setReopenOpenings(""); setPickedHm(""); setPickedRecruiters([]); setEditForm({});
    } catch (e) {
      flash(e.message || "Failed", "err");
      notify(e.message || "Failed", { title: "Error", variant: "error" });
    } finally { setBusy(false); setBusyTag(null); }
  };

  if (loading || !r) {
    return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-bg1 rounded-2xl p-6 text-t2 text-[15px]">{loading ? "Loading…" : "Request not found"}</div>
    </div>;
  }

  const statusColor = REQ_STATUS_COLOR[r.status] || K.t3;
  const fmtDate = d => d ? `${fmtDMY(d)}, ${new Date(d).toLocaleTimeString('en-IN')}` : "—";
  const fmtMoneyV = v => v >= 100000 ? "₹" + (v / 100000).toFixed(1) + "L" : v ? "₹" + Number(v).toLocaleString('en-IN') : "—";

  return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[1047px] max-h-[90vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
        <div className="min-w-0">
          <div className="text-[17px] font-extrabold text-t0 truncate">{r.position}</div>
          <div className="flex items-center gap-2 text-[14px] text-t3 mt-[2px] flex-wrap">
            <Bg c={statusColor} sm>{r.status}</Bg>
            {/* Priority pill — clickable for HR/admin on any pre-terminal
                stage. Bumps or drops priority as an operational signal
                without unlocking the whole request. */}
            {canHR && !["Closed", "Rejected"].includes(r.status) ? (
              <button
                onClick={() => setPriorityMenuOpen(v => !v)}
                title="Change priority"
                className="bg-transparent border-0 p-0 cursor-pointer relative"
              >
                <Bg c={PRIORITY_COLOR[r.urgency] || K.t3} sm>{r.urgency} ▾</Bg>
                {priorityMenuOpen && (
                  <div
                    className="absolute top-[100%] left-0 mt-1 bg-bg1 border border-b0 rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.12)] z-10 min-w-[110px] text-left"
                    onClick={e => e.stopPropagation()}
                  >
                    {PRIORITIES.map(p => (
                      <button
                        key={p}
                        onClick={async () => {
                          setPriorityMenuOpen(false);
                          if (p !== r.urgency) await act("", { urgency: p }, "priority");
                        }}
                        className={`block w-full text-left px-3 py-[6px] text-[14px] bg-transparent border-0 cursor-pointer hover:bg-bg2 ${p === r.urgency ? "font-bold text-p" : "text-t1"}`}
                      >
                        {p}{p === r.urgency ? " · current" : ""}
                      </button>
                    ))}
                  </div>
                )}
              </button>
            ) : (
              <Bg c={PRIORITY_COLOR[r.urgency] || K.t3} sm>{r.urgency}</Bg>
            )}
            <span>· {r.number_of_openings} opening{r.number_of_openings === 1 ? "" : "s"}</span>
          </div>
        </div>
        <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={20} /></button>
      </div>

      <div className="p-5">
        {msg && <div className={`px-3 py-2 mb-3 rounded-lg text-[14px] font-semibold ${msg.kind === "err" ? "bg-rb text-r border border-rd" : "bg-gb text-g border border-gd"}`}>{msg.text}</div>}

        {/* Info grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
          {[
            ["Company", r.company_name || "—"],
            ["Department", r.department || "—"],
            ["Office", r.office_name || "—"],
            ["Raised By", r.raised_by_name || r.raised_by],
            ["Raised On", r.created_at ? fmtDMY(r.created_at) : "—"],
            ["Expected Join", r.expected_join_date ? fmtDMY(r.expected_join_date) : "—"],
            ["Budget", r.budget_min || r.budget_max ? `${fmtMoneyV(r.budget_min)} – ${fmtMoneyV(r.budget_max)}` : "—"],
            ["Min. Experience", r.min_experience_years != null ? `${r.min_experience_years} yr${r.min_experience_years === 1 ? '' : 's'}` : "—"],
            ["Min. Qualification", r.min_qualification || "—"],
            ["Hiring Manager", r.hiring_manager_name || (r.status === "Approved" ? <span className="text-a">Pick before sourcing</span> : "—")],
            ["Allocated Recruiters",
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {(r.allocated_recruiters && r.allocated_recruiters.length)
                    ? <span className="text-[14px] text-t0">{r.allocated_recruiters.map(x => x.name || x.id).join(", ")}</span>
                    : (r.status === "CH-Approved" ? <span className="text-a">Pending HR allocation</span> : "—")}
                </div>
                {canManageRecruiters && ["Approved", "Sourcing", "On Hold"].includes(r.status) && !recMode && (
                  <button type="button" className="text-[12px] font-semibold text-p cursor-pointer bg-transparent border-0 flex-shrink-0"
                    onClick={() => { setPickedRecruiters((r.allocated_recruiters || []).map(x => x.id)); setRecMode(true); }}>Edit</button>
                )}
              </div>],
          ].map(([l, v]) => <div key={l} className="bg-bg2 rounded-lg p-2 border border-b0">
            <div className="text-[12px] text-t3 uppercase">{l}</div>
            <div className="text-[14px] font-semibold text-t0 mt-[1px]">{v}</div>
          </div>)}
        </div>

        {recMode && <div className="mb-4 p-3 rounded-lg bg-bg2 border border-b0">
          <div className="text-[14px] font-semibold text-t0 mb-2 flex items-center gap-1"><UserCheck size={13} color={K.p} />Add / change recruiters</div>
          <div className="text-[14px] text-t3 mb-2">Tick everyone who should work on this request. New recruiters get notified; anyone you untick loses access to it. At least one is required.</div>
          <div className="max-h-[180px] overflow-y-auto bg-bg1 border border-b0 rounded p-2 space-y-1">
            {employees
              .filter(em => (pickedRecruiters.includes(em.id) || isRecruiterCandidate(em, extraRecruiterIds)))
              .map(em => {
                const on = pickedRecruiters.includes(em.id);
                return <label key={em.id} className="flex items-center gap-2 cursor-pointer px-2 py-[6px] rounded hover:bg-bg2">
                  <input type="checkbox" checked={on} onChange={() => setPickedRecruiters(on ? pickedRecruiters.filter(x => x !== em.id) : [...pickedRecruiters, em.id])} />
                  <span className="text-[14px] text-t0">{em.nm}</span>
                  <span className="text-[12px] text-t3">· {em.rl?.t || em.rl?.title}</span>
                </label>;
              })}
          </div>
          <div className="flex gap-2 mt-2">
            <Bt v="success" s="sm" icon={Check} onClick={() => act("/recruiters", { recruiter_ids: pickedRecruiters }, "recruiters")} disabled={busy || pickedRecruiters.length === 0}>{busy && busyTag === "recruiters" ? "Saving…" : `Save (${pickedRecruiters.length})`}</Bt>
            <Bt v="secondary" s="sm" onClick={() => { setRecMode(false); setPickedRecruiters([]); }} disabled={busy}>Cancel</Bt>
          </div>
        </div>}

        {/* Justification */}
        <div className="mb-4">
          <div className="text-[14px] text-t3 uppercase mb-1">Justification</div>
          <div className="bg-bg2 border border-b0 rounded-lg p-3 text-[14px] text-t0 whitespace-pre-wrap">{r.justification}</div>
        </div>

        {/* Skills */}
        {r.skills_required && <div className="mb-4">
          <div className="text-[14px] text-t3 uppercase mb-1">Skills Required</div>
          <div className="bg-bg2 border border-b0 rounded-lg p-3 text-[14px] text-t0 whitespace-pre-wrap">{r.skills_required}</div>
        </div>}

        {/* Job Description attachment — re-upload replaces the file in
             place (same endpoint the initial upload uses); the section
             also now shows for someone who CAN attach one even before any
             JD exists, not just after. */}
        {(r.jd_file_path || canHR || r.raised_by === myEmpId) && <div className="mb-4">
          <div className="text-[14px] text-t3 uppercase mb-1">Job Description</div>
          <div className="flex items-center gap-2">
            {r.jd_file_path ? (
              <a href={`${BASE}${r.jd_file_path}`} target="_blank" rel="noreferrer" className="flex-1 min-w-0 flex items-center gap-2 bg-bg2 border border-b0 rounded-lg p-3 text-[14px] text-p font-semibold no-underline hover:bg-sh">
                <FileText size={14} color={K.p} />
                <span className="flex-1 truncate">{r.jd_file_name || "Download JD"}</span>
                <span className="text-[12px] text-t3 uppercase">Open</span>
              </a>
            ) : (
              <div className="flex-1 flex items-center gap-2 bg-bg2 border border-b0 rounded-lg p-3 text-[14px] text-t3">
                <FileText size={14} color={K.t3} />
                <span>No JD attached yet</span>
              </div>
            )}
            {(canHR || r.raised_by === myEmpId) && (
              <>
                <input ref={jdInputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => { uploadAttachment(e.target.files?.[0], "jd"); e.target.value = ""; }} />
                <Bt v="secondary" s="sm" disabled={jdUploading} onClick={() => jdInputRef.current?.click()}>
                  {jdUploading ? "Uploading…" : r.jd_file_path ? "Re-upload" : "Upload JD"}
                </Bt>
              </>
            )}
          </div>
        </div>}

        {/* Assessment / test paper — only shown when test_required. HR
             sees the file even if just the flag is set (so they know
             they need to upload one). */}
        {r.test_required && <div className="mb-4">
          <div className="text-[14px] text-t3 uppercase mb-1">Assessment / Test Paper</div>
          <div className="flex items-center gap-2">
            {r.test_file_path ? (
              <a href={`${BASE}${r.test_file_path}`} target="_blank" rel="noreferrer" className="flex-1 min-w-0 flex items-center gap-2 bg-bg2 border border-b0 rounded-lg p-3 text-[14px] text-p font-semibold no-underline hover:bg-sh">
                <FileText size={14} color={K.p} />
                <span className="flex-1 truncate">{r.test_file_name || "Download Test Paper"}</span>
                <span className="text-[12px] text-t3 uppercase">Open</span>
              </a>
            ) : (
              <div className="flex-1 flex items-center gap-2 bg-ab border border-ad rounded-lg p-3 text-[14px] text-a">
                <AlertTriangle size={13} />
                <span>Test required but no paper uploaded yet — HR should attach it before sourcing starts.</span>
              </div>
            )}
            {(canHR || r.raised_by === myEmpId) && (
              <>
                <input ref={testInputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => { uploadAttachment(e.target.files?.[0], "test"); e.target.value = ""; }} />
                <Bt v="secondary" s="sm" disabled={testUploading} onClick={() => testInputRef.current?.click()}>
                  {testUploading ? "Uploading…" : r.test_file_path ? "Re-upload" : "Upload"}
                </Bt>
              </>
            )}
          </div>
        </div>}

        {/* Candidates rolled up against this requisition. Anyone with
             visibility on the request can see the count + a mini-list.
             Includes an "X of N filled" strip so it's obvious when a
             requisition still has open positions to source for. */}
        {cands && <div className="mb-4 p-3 rounded-lg bg-bg2 border border-b0">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[14px] text-t3 uppercase">Candidates ({cands.counts?.total || 0})</div>
            <div className="text-[14px] font-semibold">
              <span className={cands.remaining === 0 && cands.openings > 0 ? "text-g" : "text-t2"}>
                Hired: {cands.hired || 0} of {cands.openings || 0}
              </span>
              {cands.remaining === 0 && cands.openings > 0 && r.status === "Sourcing" && (
                <span className="ml-2 text-[12px] text-a">· All openings filled — consider closing.</span>
              )}
            </div>
          </div>
          {(cands.counts?.total || 0) > 0 ? (
            <>
              <div className="flex gap-1 flex-wrap mb-2">
                {["New", "Interviewing", "Offer", "Hired", "Rejected", "Not Joined"].map(s => cands.counts?.[s] ? (
                  <span key={s} className="px-2 py-[3px] rounded bg-bg1 border border-b0 text-[14px] text-t1">
                    <span className="font-bold">{cands.counts[s]}</span> {stageLbl(s)}
                  </span>
                ) : null)}
              </div>
              <div className="max-h-[160px] overflow-auto rounded border border-b0 bg-bg1">
                <table className="w-full min-w-[420px] border-collapse text-[14px]">
                  <thead><tr className="bg-bg2">
                    <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Candidate</th>
                    <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Stage</th>
                    <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase">Added</th>
                    <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase"></th>
                  </tr></thead>
                  <tbody>
                    {cands.rows.slice(0, 20).map(c => (
                      <tr key={c.id} className="border-b border-b0 hover:bg-sh">
                        <td className="px-2 py-[6px] text-[14px] text-t0">{c.name} <span className="text-t3 text-[12px]">· {c.email || c.phone || c.id}</span></td>
                        <td className="px-2 py-[6px]"><Bg c={STAGE_COLORS[STAGES.indexOf(c.stage)] || (c.stage === "Rejected" ? K.r : c.stage === "Not Joined" ? K.a : c.stage === "Withdrawn" ? K.pu : K.t3)} sm>{stageLbl(c.stage || "New")}</Bg></td>
                        <td className="px-2 py-[6px] text-right text-[12px] text-t3">{c.created_at ? fmtDMY(c.created_at) : "—"}</td>
                        <td className="px-2 py-[6px] text-right"><Bt v="ghost" s="xs" icon={Eye} onClick={() => setOpenCand(c.id)}>View Profile</Bt></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {cands.rows.length > 20 && (
                  <div className="p-2 text-center text-[14px] text-t3">Showing 20 of {cands.rows.length} — full list on the Candidates tab.</div>
                )}
              </div>
            </>
          ) : (
            <div className="text-[14px] text-t3 py-2 text-center">No candidates attached yet.</div>
          )}
        </div>}

        {/* Decision history */}
        {(r.ch_approved_at || r.approved_at || r.sourcing_started_at || r.closed_at || r.reopened_at) && <div className="mb-4 p-3 rounded-lg bg-bg2 border border-b0">
          <div className="text-[14px] text-t3 uppercase mb-2">Decision Trail</div>
          <div className="space-y-1 text-[14px]">
            {r.ch_approved_at && <div className="flex items-center gap-2"><Check size={11} color={K.bl} /><span className="text-t2">CH-approved by {r.ch_approved_by_name || r.ch_approved_by} on {fmtDate(r.ch_approved_at)}</span></div>}
            {r.approved_at && <div className="flex items-center gap-2"><Check size={11} color={r.status === "Rejected" ? K.r : K.g} /><span className="text-t2">{r.status === "Rejected" ? "Rejected" : "HR-approved"} by {r.approved_by_name || r.approved_by} on {fmtDate(r.approved_at)}</span></div>}
            {r.sourcing_started_at && <div className="flex items-center gap-2"><PlayCircle size={11} color={K.p} /><span className="text-t2">Sourcing started on {fmtDate(r.sourcing_started_at)}</span></div>}
            {r.closed_at && <div className="flex items-center gap-2"><XCircle size={11} color={K.t3} /><span className="text-t2">Closed on {fmtDate(r.closed_at)}{r.closed_reason ? ` · ${r.closed_reason}` : ""}</span></div>}
            {r.reopened_at && <div className="flex items-center gap-2"><RotateCcw size={11} color={K.g} /><span className="text-t2">Reopened on {fmtDate(r.reopened_at)}{r.reopened_by ? ` by ${r.reopened_by}` : ""}{r.reopen_reason ? ` · ${r.reopen_reason}` : ""}</span></div>}
            {r.rejection_reason && <div className="mt-1 p-2 rounded bg-rb border border-rd text-r text-[14px]"><span className="font-semibold">Rejection reason: </span>{r.rejection_reason}</div>}
          </div>
        </div>}

        {/* Actions — stage- and role-gated. */}
        {(() => {
          const iAmAllocated = !!(r.allocated_recruiters || []).find(x => x.id === myEmpId);
          const iAmRaiser = r.raised_by === myEmpId;
          const showCHActions = canCH && r.status === "Pending" && !iAmRaiser;
          const showHRApprove = canHR && r.status === "CH-Approved";
          // Edit is allowed before recruiters are allocated, i.e. Pending or
          // CH-Approved. Raiser can fix their own; HR Head can adjust scope.
          const showEdit = ["Pending", "CH-Approved"].includes(r.status) && (iAmRaiser || canHR);
          const canStartSourcing = r.status === "Approved" && (canHR || iAmAllocated);
          const canHold = canHR && ["Approved", "Sourcing"].includes(r.status);
          const canResume = canHR && r.status === "On Hold";
          const canClose = canHR && ["Approved", "Sourcing", "On Hold"].includes(r.status);
          // A close is reversible — a hire falls through, or headcount is
          // topped up. Rejections are not: those go back to CH/HR as a
          // fresh request so the approval trail stays honest.
          const canReopen = canHR && r.status === "Closed";
          const canReject = (canCH && r.status === "Pending" && !iAmRaiser) || (canHR && r.status === "CH-Approved");
          const canDelete = can('recruitment', 'requests', 'delete_request', 'edit');
          const anyAction = canDelete || showCHActions || showHRApprove || showEdit || canStartSourcing || canHold || canResume || canClose || canReopen;
          if (!anyAction) {
            if (r.status === "Pending") return <div className="text-[14px] text-t3 text-center py-2 border-t border-b0">Awaiting Centre Head approval.</div>;
            if (r.status === "CH-Approved") return <div className="text-[14px] text-t3 text-center py-2 border-t border-b0">Awaiting HR Head approval + recruiter allocation.</div>;
            if (r.status === "Approved") return <div className="text-[14px] text-t3 text-center py-2 border-t border-b0">Approved — allocated recruiters can start sourcing.</div>;
            if (r.status === "On Hold") return <div className="text-[14px] text-t3 text-center py-2 border-t border-b0">On hold — HR Head can resume when ready.</div>;
            if (r.status === "Closed") return <div className="text-[14px] text-t3 text-center py-2 border-t border-b0">Closed — only HR Head / Admin can reopen it.</div>;
            if (r.status === "Rejected") return <div className="text-[14px] text-t3 text-center py-2 border-t border-b0">No further actions — this request is rejected. Raise a fresh request instead.</div>;
            return null;
          }
          return <div className="border-t border-b0 pt-4">
            <div className="text-[14px] text-t3 uppercase mb-2">Actions</div>
            <div className="flex gap-2 flex-wrap">
              {showEdit && !editMode && !allocateMode && !rejectMode && !sourcingMode && !closeMode && (
                <Bt v="secondary" s="sm" icon={Edit} onClick={() => { setEditForm({
                  position: r.position || "", department_id: r.department_id ? String(r.department_id) : "",
                  office_id: r.office_id ? String(r.office_id) : "",
                  number_of_openings: r.number_of_openings || 1, urgency: r.urgency || "Medium",
                  justification: r.justification || "", skills_required: r.skills_required || "",
                  budget_min: r.budget_min || "", budget_max: r.budget_max || "",
                  expected_join_date: r.expected_join_date ? String(r.expected_join_date).slice(0, 10) : "",
                  hiring_manager_id: r.hiring_manager_id || "",
                }); setEditMode(true); }} disabled={busy}>Edit</Bt>
              )}
              {showCHActions && <>
                <Bt v="success" s="sm" icon={Check} onClick={() => act("/ch-approve", null, "ch-approve")} disabled={busy}>{busy && busyTag === "ch-approve" ? "Approving…" : "Approve (CH)"}</Bt>
                {!rejectMode && <Bt v="danger" s="sm" icon={XCircle} onClick={() => setRejectMode(true)} disabled={busy}>Reject</Bt>}
              </>}
              {showHRApprove && <>
                {!allocateMode && <Bt v="success" s="sm" icon={Check} onClick={() => { setPickedRecruiters([]); setAllocateMode(true); }} disabled={busy}>Approve &amp; Allocate</Bt>}
                {!rejectMode && !allocateMode && <Bt v="danger" s="sm" icon={XCircle} onClick={() => setRejectMode(true)} disabled={busy}>Reject</Bt>}
              </>}
              {canStartSourcing && !sourcingMode && <Bt v="success" s="sm" icon={PlayCircle} onClick={() => { if (r.hiring_manager_id) { act("/start-sourcing", null, "start-sourcing"); } else { setPickedHm(""); setSourcingMode(true); } }} disabled={busy}>{busy && busyTag === "start-sourcing" ? "Starting…" : "Start Sourcing"}</Bt>}
              {canHold && !editMode && !closeMode && !sourcingMode && <Bt v="warning" s="sm" icon={PauseCircle} onClick={() => act("/on-hold", null, "on-hold")} disabled={busy}>{busy && busyTag === "on-hold" ? "Putting On Hold…" : "Put On Hold"}</Bt>}
              {canResume && <Bt v="success" s="sm" icon={PlayCircle} onClick={() => act("/resume", null, "resume")} disabled={busy}>{busy && busyTag === "resume" ? "Resuming…" : "Resume"}</Bt>}
              {canClose && !closeMode && !sourcingMode && !editMode && <Bt v="secondary" s="sm" onClick={() => setCloseMode(true)} disabled={busy}>Close</Bt>}
              {canDelete && !editMode && !allocateMode && !rejectMode && !sourcingMode && !closeMode && !reopenMode && <Bt v="danger" s="sm" icon={Trash2} onClick={async () => {
                if (!(await confirmDialog(`Delete the hiring request "${r.position}"? This cannot be undone.`))) return;
                setBusy(true); setBusyTag("delete");
                try {
                  await api.del(`/api/recruitment-requests/${id}`);
                  notify("Hiring request deleted", { title: "Deleted", variant: "success" });
                  onRefresh?.(); onClose?.();
                } catch (e) { notify(e.message || "Failed to delete", { title: "Error", variant: "error" }); }
                finally { setBusy(false); setBusyTag(null); }
              }} disabled={busy}>{busy && busyTag === "delete" ? "Deleting…" : "Delete"}</Bt>}
              {canReopen && !reopenMode && <Bt v="success" s="sm" icon={RotateCcw} onClick={() => {
                // Pre-fill one more head than already hired: reopening a
                // requisition that auto-closed on its last hire is pointless
                // unless the count goes up, and that's the usual reason.
                const hired = cands?.hired || 0;
                setReopenOpenings(String(Math.max(r.number_of_openings || 1, hired + 1)));
                setReopenReason(""); setReopenMode(true);
              }} disabled={busy}>Reopen Request</Bt>}
            </div>

            {allocateMode && <div className="mt-3 p-3 rounded-lg bg-bg2 border border-b0">
              <div className="text-[14px] font-semibold text-t0 mb-2 flex items-center gap-1"><UserCheck size={13} color={K.p} />Allocate recruiter(s)</div>
              <div className="text-[14px] text-t3 mb-2">Every recruiter you pick will see this hiring request and can start sourcing candidates against it. Tick one or more.</div>
              <div className="max-h-[180px] overflow-y-auto bg-bg1 border border-b0 rounded p-2 space-y-1">
                {employees
                  .filter(em => isRecruiterCandidate(em, extraRecruiterIds))
                  .map(em => {
                    const on = pickedRecruiters.includes(em.id);
                    return <label key={em.id} className="flex items-center gap-2 cursor-pointer px-2 py-[6px] rounded hover:bg-bg2">
                      <input type="checkbox" checked={on} onChange={() => setPickedRecruiters(on ? pickedRecruiters.filter(x => x !== em.id) : [...pickedRecruiters, em.id])} />
                      <span className="text-[14px] text-t0">{em.nm}</span>
                      <span className="text-[12px] text-t3">· {em.rl?.t || em.rl?.title}</span>
                    </label>;
                  })}
                {employees.filter(em => isRecruiterCandidate(em, extraRecruiterIds)).length === 0 && (
                  <div className="text-[14px] text-t3 py-2 text-center">No active recruiters found. Assign the Recruiter / HR / Talent role to an employee first.</div>
                )}
              </div>
              <div className="flex gap-2 mt-2">
                <Bt v="success" s="sm" icon={Check} onClick={() => act("/approve", { recruiter_ids: pickedRecruiters }, "approve")} disabled={busy || pickedRecruiters.length === 0}>{busy && busyTag === "approve" ? "Approving…" : `Confirm (${pickedRecruiters.length})`}</Bt>
                <Bt v="secondary" s="sm" onClick={() => { setAllocateMode(false); setPickedRecruiters([]); }} disabled={busy}>Cancel</Bt>
              </div>
            </div>}

            {rejectMode && <div className="mt-3 p-3 rounded-lg bg-rb border border-rd">
              <div className="text-[14px] font-semibold text-r mb-2 flex items-center gap-1"><AlertTriangle size={13} />Reject this request</div>
              <textarea value={rejReason} onChange={e => setRejReason(e.target.value)} placeholder="Reason — budget cut, deferred to next quarter, role redefined…" className="w-full min-h-[60px] p-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] outline-none" />
              <div className="flex gap-2 mt-2">
                <Bt v="danger" s="sm" icon={X} onClick={() => act("/reject", { reason: rejReason.trim() }, "reject")} disabled={busy || !rejReason.trim() || !canReject}>{busy && busyTag === "reject" ? "Rejecting…" : "Confirm Reject"}</Bt>
                <Bt v="secondary" s="sm" onClick={() => { setRejectMode(false); setRejReason(""); }} disabled={busy}>Cancel</Bt>
              </div>
            </div>}

            {sourcingMode && <div className="mt-3 p-3 rounded-lg bg-bg2 border border-b0">
              <div className="text-[14px] font-semibold text-t0 mb-2 flex items-center gap-1"><UserCheck size={13} color={K.p} />Pick a hiring manager</div>
              <div className="text-[14px] text-t3 mb-2">A hiring manager owns this role through to offer. They'll appear on every candidate sourced for this requisition.</div>
              <select value={pickedHm} onChange={e => setPickedHm(e.target.value)} className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none">
                <option value="">Select hiring manager…</option>
                {employees.filter(em => em.st === 'Active').map(em => <option key={em.id} value={em.id}>{em.nm}</option>)}
              </select>
              <div className="flex gap-2 mt-2">
                <Bt v="success" s="sm" icon={PlayCircle} onClick={() => act("/start-sourcing", { hiring_manager_id: pickedHm }, "start-sourcing")} disabled={busy || !pickedHm}>{busy && busyTag === "start-sourcing" ? "Starting…" : "Confirm & Start Sourcing"}</Bt>
                <Bt v="secondary" s="sm" onClick={() => { setSourcingMode(false); setPickedHm(""); }} disabled={busy}>Cancel</Bt>
              </div>
            </div>}

            {reopenMode && <div className="mt-3 p-3 rounded-lg bg-gb border border-gd">
              <div className="text-[14px] font-semibold text-t0 mb-2 flex items-center gap-1"><RotateCcw size={13} color={K.g} />Reopen this requisition</div>
              <div className="text-[14px] text-t3 mb-2">
                It goes back to <b>{r.closed_from_status || (r.sourcing_started_at ? "Sourcing" : "Approved")}</b> — the approvals already on the trail stand, nothing needs re-approving.
                {(cands?.hired || 0) > 0 && <> {cands.hired} candidate(s) are already hired against it, so openings must go above that.</>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <div className="text-[14px] text-t3 mb-1">Openings</div>
                  <input type="number" min={Math.max(1, (cands?.hired || 0) + 1)} value={reopenOpenings} onChange={e => setReopenOpenings(e.target.value)} className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none" />
                </div>
                <div className="sm:col-span-2">
                  <div className="text-[14px] text-t3 mb-1">Why reopen?</div>
                  <input value={reopenReason} onChange={e => setReopenReason(e.target.value)} placeholder="Candidate didn't join / extra headcount approved / backfill…" className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] outline-none" />
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <Bt v="success" s="sm" icon={RotateCcw} onClick={() => act("/reopen", { reason: reopenReason.trim(), number_of_openings: reopenOpenings }, "reopen")} disabled={busy || !reopenReason.trim() || !reopenOpenings}>{busy && busyTag === "reopen" ? "Reopening…" : "Confirm Reopen"}</Bt>
                <Bt v="secondary" s="sm" onClick={() => { setReopenMode(false); setReopenReason(""); setReopenOpenings(""); }} disabled={busy}>Cancel</Bt>
              </div>
            </div>}

            {closeMode && <div className="mt-3 p-3 rounded-lg bg-bg2 border border-b0">
              <div className="text-[14px] font-semibold text-t0 mb-2">Close this requisition</div>
              <input value={closeReason} onChange={e => setCloseReason(e.target.value)} placeholder="Filled / cancelled / deferred — optional" className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] outline-none" />
              <div className="flex gap-2 mt-2">
                <Bt v="success" s="sm" onClick={() => act("/close", { reason: closeReason.trim() }, "close")} disabled={busy}>{busy && busyTag === "close" ? "Closing…" : "Confirm Close"}</Bt>
                <Bt v="secondary" s="sm" onClick={() => { setCloseMode(false); setCloseReason(""); }} disabled={busy}>Cancel</Bt>
              </div>
            </div>}

            {editMode && <div className="mt-3 p-3 rounded-lg bg-bg2 border border-b0">
              <div className="text-[14px] font-semibold text-t0 mb-2 flex items-center gap-1"><Edit size={13} color={K.p} />Edit request</div>
              <div className="text-[14px] text-t3 mb-2">Only Priority, Openings, Justification, Skills, Budget and Expected Join Date can be tweaked once the request is in flight. Position &amp; centre are locked to keep decision trails coherent.</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <div className="text-[14px] text-t3 mb-1">Position</div>
                  <input className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none" value={editForm.position || ""} onChange={e => setEditForm(f => ({ ...f, position: e.target.value }))} />
                </div>
                <div>
                  <div className="text-[14px] text-t3 mb-1">Priority</div>
                  <select className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none" value={editForm.urgency || "Medium"} onChange={e => setEditForm(f => ({ ...f, urgency: e.target.value }))}>
                    {PRIORITIES.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-[14px] text-t3 mb-1">Openings</div>
                  <input type="number" min="1" className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none" value={editForm.number_of_openings || 1} onChange={e => setEditForm(f => ({ ...f, number_of_openings: e.target.value }))} />
                </div>
                <div>
                  <div className="text-[14px] text-t3 mb-1">Expected Join</div>
                  <input type="date" className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none" value={editForm.expected_join_date || ""} onChange={e => setEditForm(f => ({ ...f, expected_join_date: e.target.value }))} />
                </div>
                <div>
                  <div className="text-[14px] text-t3 mb-1">Budget Min (₹)</div>
                  <input type="number" className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none" value={editForm.budget_min || ""} onChange={e => setEditForm(f => ({ ...f, budget_min: e.target.value }))} />
                </div>
                <div>
                  <div className="text-[14px] text-t3 mb-1">Budget Max (₹)</div>
                  <input type="number" className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none" value={editForm.budget_max || ""} onChange={e => setEditForm(f => ({ ...f, budget_max: e.target.value }))} />
                </div>
                <div className="sm:col-span-2">
                  <div className="text-[14px] text-t3 mb-1">Justification</div>
                  <textarea className="w-full min-h-[60px] p-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] outline-none" value={editForm.justification || ""} onChange={e => setEditForm(f => ({ ...f, justification: e.target.value }))} />
                </div>
                <div className="sm:col-span-2">
                  <div className="text-[14px] text-t3 mb-1">Skills required</div>
                  <textarea className="w-full min-h-[50px] p-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] outline-none" value={editForm.skills_required || ""} onChange={e => setEditForm(f => ({ ...f, skills_required: e.target.value }))} />
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <Bt v="success" s="sm" icon={Check} onClick={() => act("", {
                  position: editForm.position,
                  urgency: editForm.urgency,
                  number_of_openings: editForm.number_of_openings,
                  justification: editForm.justification,
                  skills_required: editForm.skills_required,
                  budget_min: editForm.budget_min || null,
                  budget_max: editForm.budget_max || null,
                  expected_join_date: editForm.expected_join_date || null,
                }, "edit")} disabled={busy}>{busy && busyTag === "edit" ? "Saving…" : "Save Changes"}</Bt>
                <Bt v="secondary" s="sm" onClick={() => { setEditMode(false); setEditForm({}); }} disabled={busy}>Cancel</Bt>
              </div>
            </div>}
          </div>;
        })()}
      </div>
    </div>

    {/* Candidate profile from the mini-list — HR / hiring manager can
         drill into a full profile without leaving the requisition. */}
    {openCand && <CandidateDetail
      candId={openCand}
      onClose={() => setOpenCand(null)}
      onRefresh={() => { loadCands(); onRefresh?.(); }}
      canEdit={can('recruitment', 'candidates', null, 'edit')}
      canManagePostSelection={can('recruitment', 'candidates', 'manage_post_selection', 'edit')}
      canDelete={can('recruitment', 'candidates', 'delete_candidate', 'delete')}
      employees={employees}
      offices={offices}
    />}
  </div>;
};

// HR Head / Admin: choose who else (besides people with a recruiting / HR / talent job role) can be picked as a recruiter.
const RecruiterPoolModal = ({ employees = [], ids = [], onClose, onSaved }) => {
  const [list, setList] = useState(ids);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const byId = new Map(employees.map(e => [String(e.id), e]));
  const matches = q.trim().length < 2 ? [] : employees
    .filter(e => e.st === "Active" && !list.includes(String(e.id)) && !isRecruiterCandidate(e) && ((e.nm || "").toLowerCase().includes(q.trim().toLowerCase()) || String(e.id).toLowerCase().includes(q.trim().toLowerCase())))
    .slice(0, 8);
  const save = async () => {
    setSaving(true);
    try { const d = await api.put("/api/recruitment/recruiter-pool", { employee_ids: list }); onSaved((d?.employee_ids || list).map(String)); notify("Recruiter list saved", { title: "Saved", variant: "success" }); onClose(); }
    catch (e) { notify(e.message || "Couldn't save the recruiter list", { title: "Error", variant: "error" }); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[700] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-[460px] bg-bg1 border border-b0 rounded-2xl shadow-xl p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1"><div className="text-[15px] font-extrabold text-t0">Recruiter list</div><button onClick={onClose} className="w-[28px] h-[28px] rounded-full bg-bg2 border-0 cursor-pointer flex items-center justify-center text-t2"><X size={15} /></button></div>
        <div className="text-[13px] text-t3 mb-3">People with a recruiting, HR or talent job role already appear when picking a recruiter. Add anyone else here so they can be picked too.</div>
        <div className="text-[12px] font-semibold text-t3 uppercase mb-1">Added people</div>
        <div className="max-h-[170px] overflow-y-auto border border-b0 rounded-lg mb-3">
          {list.length === 0 && <div className="p-3 text-[13px] text-t3 text-center">No one added yet.</div>}
          {list.map(id => { const e = byId.get(String(id)); return (
            <div key={id} className="flex items-center gap-2 px-3 py-[7px] border-b border-b0 last:border-b-0">
              <Av nm={e?.nm || id} sz={24} /><div className="flex-1 min-w-0"><div className="text-[14px] font-semibold text-t0 truncate">{e?.nm || id}</div><div className="text-[11px] text-t3">{e ? `${id} · ${e.rl?.t || e.rl?.title || ""}` : id}</div></div>
              <Bt v="danger" s="xs" icon={X} onClick={() => setList(list.filter(x => x !== id))}>Remove</Bt>
            </div>); })}
        </div>
        <div className="text-[12px] font-semibold text-t3 uppercase mb-1">Add a person</div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Type a name or employee ID…" className="w-full px-3 py-2 bg-bg2 border border-b0 rounded-lg text-[14px] text-t0 outline-none" />
        {matches.length > 0 && <div className="mt-1 border border-b0 rounded-lg overflow-hidden">
          {matches.map(e => <button key={e.id} onClick={() => { setList([...list, String(e.id)]); setQ(""); }} className="w-full flex items-center gap-2 px-3 py-[7px] bg-transparent border-0 border-b border-b0 cursor-pointer text-left hover:bg-bg2">
            <Av nm={e.nm} sz={22} /><span className="text-[14px] text-t0">{e.nm}</span><span className="text-[11px] text-t3">· {e.id} · {e.rl?.t || e.rl?.title || ""}</span><Plus size={14} className="ml-auto text-p" />
          </button>)}
        </div>}
        {q.trim().length >= 2 && matches.length === 0 && <div className="mt-1 text-[12px] text-t3">No matching active employee (people already shown in the recruiter list are left out).</div>}
        <div className="flex justify-end gap-2 mt-4"><Bt v="secondary" onClick={onClose} disabled={saving}>Cancel</Bt><Bt onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Bt></div>
      </div>
    </div>
  );
};

export const RecruitmentPage = ({ roles = [], role, companies = [], departments = [], offices = [], employees = [], myProfile, hasInterviews = false, hashQuery, onRefresh, can = () => true }) => {
  const [showForm, setShowForm] = useState(null);
  // Extra recruiters (people picked as recruiters without a recruiting / HR / talent job role) — see RecruiterPoolModal.
  const [recruiterPool, setRecruiterPool] = useState([]);
  const [showPool, setShowPool] = useState(false);
  useEffect(() => { api.get("/api/recruitment/recruiter-pool").then(d => setRecruiterPool((d?.employee_ids || []).map(String))).catch(() => {}); }, []);
  const canManagePool = can("recruitment", "requests", "manage_recruiter_pool", "edit"); // Settings → Permissions › Recruitment › Hiring Requests
  // "#recruitment?candidate=<id>" (interview notifications) opens that profile directly.
  const [openCand, setOpenCand] = useState(hashQuery?.candidate || null);
  useEffect(() => { if (hashQuery?.candidate) setOpenCand(hashQuery.candidate); }, [hashQuery?.candidate]);

  // Role flags with distinct meanings:
  //   isHr                  — broad page access (sees Candidates + Hiring Requests tabs)
  //   canEdit               — write access on candidate creation / interview rounds
  //   canManagePostSelection — write access on a candidate's post-selection /
  //                            offer workflow (stage moves, docs, BGV, portal link)
  //   isApprover            — can approve/reject/start-sourcing/close hiring requests
  // Centre Head raises hiring requests and, once a candidate lands in their
  // centre, owns getting them through offer + joining — but can't add a
  // candidate, manage interview rounds, or approve requisitions.
  const isHr = can('recruitment', 'candidates', null, 'view');
  const canEdit = can('recruitment', 'candidates', null, 'edit');
  const canManagePostSelection = can('recruitment', 'candidates', 'manage_post_selection', 'edit');
  const isApprover = can('recruitment', 'requests', 'hr_approve', 'edit');
  // Raising a NEW hiring request is narrower than isHr: admin/hr_head/
  // centre_head always can, but a recruiter only can if the admin panel's
  // per-employee "Can Raise Hiring Requests" toggle is on for them —
  // recruiters otherwise just work requests already allocated to them.
  const canAlwaysRaise = can('recruitment', 'requests', 'raise_without_limit', 'edit');
  const canRaiseHiring = can('recruitment', 'requests', 'raise_request', 'edit');
  const canSeeRequestsTab = isHr || canRaiseHiring;
  const canSeeMyInterviewsTab = hasInterviews;

  // Non-HR users don't see the Candidates tab — landing them there just
  // shows an "No access to candidate pipeline" empty state (see the
  // !isHr branch below), so a raiser-only employee should default to
  // Hiring Requests and an interviewer to My Interviews.
  const defaultTab = isHr
    ? "candidates"
    : canRaiseHiring
      ? "requests"
      : canSeeMyInterviewsTab
        ? "myinterviews"
        : "candidates";
  // A notification link like "#recruitment?tab=requests&id=42" (see
  // requestLink() in recruitment-requests.js) lands here via App.js's
  // hashQuery — honor it over the role-based default, but only to a tab
  // this role can actually see.
  const linkedTab = hashQuery?.tab === "requests" && canSeeRequestsTab ? "requests"
    : hashQuery?.tab === "myinterviews" && canSeeMyInterviewsTab ? "myinterviews"
    : hashQuery?.tab === "candidates" && isHr ? "candidates"
    : null;
  const [recTab, setRecTab] = useState(linkedTab || defaultTab);

  // Candidate sub-tab state — Active / Hired / Rejected with date window.
  const ninetyDaysAgo = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const [candBucket, setCandBucket] = useState("active");
  const [candFrom, setCandFrom] = useState(ninetyDaysAgo);
  const [candTo, setCandTo] = useState(today);
  const [candRows, setCandRows] = useState([]);
  const [candCounts, setCandCounts] = useState({ all: 0, active: 0, hired: 0, rejected: 0, notjoined: 0, withdrawn: 0 });
  const [candLoading, setCandLoading] = useState(true);
  // Client-side narrowing over the current bucket — pos: 'all' or a
  // literal position string; stg: 'all' or one of STAGES. Reset on
  // bucket switch so a stale filter doesn't blank the new tab.
  const [candPos, setCandPos] = useState("all");
  const [candStage, setCandStage] = useState("all");
  const [candRecruiter, setCandRecruiter] = useState("all");
  // Active has no date filter by default (unchanged behavior — shows every
  // active candidate regardless of when anything last happened). Picking a
  // date field here is what turns the Period filter on for Active; "" means
  // off. Terminal buckets (Hired/Rejected/Not Joined) keep their own
  // always-on Period filter, unaffected by this.
  const [candDateField, setCandDateField] = useState("");
  const [candSearch, setCandSearch] = useState("");
  useEffect(() => { setCandPos("all"); setCandStage("all"); setCandRecruiter("all"); setCandDateField(""); setCandSearch(""); }, [candBucket]);

  const loadCands = () => {
    setCandLoading(true);
    const params = new URLSearchParams({ bucket: candBucket });
    if (candBucket !== "active" && candBucket !== "all") {
      params.set("from", candFrom); params.set("to", candTo);
    } else if (candDateField) {
      params.set("from", candFrom); params.set("to", candTo); params.set("date_field", candDateField);
    }
    api.get(`/api/recruitment?${params.toString()}`)
      .then(d => { setCandRows(d.rows || []); setCandCounts(d.counts || { all: 0, active: 0, hired: 0, rejected: 0, notjoined: 0, withdrawn: 0 }); setCandLoading(false); })
      .catch(() => setCandLoading(false));
  };
  useEffect(() => { if (recTab === "candidates" && isHr) loadCands(); /* eslint-disable-next-line */ }, [recTab, candBucket, candFrom, candTo, candDateField]);

  const pillTab = (active) => `px-4 py-[7px] rounded-lg text-[13px] font-bold transition-colors flex items-center gap-[6px] cursor-pointer border-0 whitespace-nowrap ${active ? "bg-s text-t0 shadow-[0_1px_3px_rgba(0,0,0,0.1)]" : "bg-transparent text-t2 hover:text-t0"}`;

  const showTabs = canSeeRequestsTab || canSeeMyInterviewsTab || can("reports", "activity_log", null, "view");

  const exportCandsCsv = (rowsToExport) => {
    const header = ["Candidate", "Email", "Position", "Experience", "Expected CTC", "Source", "HR/Recruiter", "Stage", "Last Updated"];
    const csvRows = rowsToExport.map(r => [r.nm, r.email || "", r.pos || "", r.exp ? r.exp + "yr" : "", r.expCtc || "", r.src || "", r.recruiterName || "", stageLbl(r.stg), r.updatedAt ? fmtDMY(r.updatedAt) : ""]);
    const csv = [header, ...csvRows].map(row => row.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `recruitment_${candBucket}_${today}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <RecruiterPoolCtx.Provider value={recruiterPool}>
    <div>
      <div className="flex justify-between items-start mb-[14px] gap-3 flex-wrap">
        <div>
          <h1 className="m-0 text-2xl font-extrabold text-t0">Recruitment</h1>
          <div className="text-[14px] text-t3 mt-[3px]">Track candidates across pipeline stages, manage hiring requests, and review interviews.</div>
        </div>
        <div className="flex gap-[6px]">
          {canManagePool && <Bt v="secondary" s="sm" icon={Users} onClick={() => setShowPool(true)}>Recruiter list</Bt>}
          {recTab === "candidates" && isHr && <Bt v="secondary" s="sm" icon={Download} onClick={() => exportCandsCsv(candRows)}>Export CSV</Bt>}
          {canEdit && recTab === "candidates" && candBucket === "active" && <Bt icon={Plus} s="sm" onClick={() => setShowForm("candidate")}>Add Candidate</Bt>}
        </div>
      </div>

      {showTabs && <div className="inline-flex p-1 bg-bg2 rounded-xl gap-[2px] mb-4 flex-wrap">
        {isHr && <button className={pillTab(recTab === "candidates")} onClick={() => setRecTab("candidates")}>Candidates</button>}
        {canSeeRequestsTab && <button className={pillTab(recTab === "requests")} onClick={() => setRecTab("requests")}>Hiring Requests</button>}
        {canSeeMyInterviewsTab && <button className={pillTab(recTab === "myinterviews")} onClick={() => setRecTab("myinterviews")}>My Interviews</button>}
        {can("recruitment", "offer_templates", null, "view") && <button className={pillTab(recTab === "templates")} onClick={() => setRecTab("templates")}>Offer Templates</button>}
        {can("reports", "activity_log", null, "view") && <button className={pillTab(recTab === "activity")} onClick={() => setRecTab("activity")}>Activity Log</button>}
      </div>}

      {recTab === "activity" ? (
        <ActivityLog module="recruitment" title="Recruitment Activity Log" canDownload={can("reports", "activity_log", "download", "view")} />
      ) : recTab === "templates" ? (
        <OfferTemplatesTab />
      ) : recTab === "myinterviews" ? (
        <MyInterviewsPage />
      ) : recTab === "requests" ? (
        <HiringRequestsTab role={role} canRaise={canRaiseHiring || canAlwaysRaise} isApprover={isApprover} myProfile={myProfile} companies={companies} departments={departments} offices={offices} employees={employees} initialOpenId={linkedTab === "requests" && hashQuery?.id ? parseInt(hashQuery.id, 10) : null} can={can} />
      ) : !isHr ? (
        <Cd><div className="text-center p-[30px] text-t3 text-[14px]">No access to candidate pipeline. Switch tabs to view your interviews or raise hiring requests.</div></Cd>
      ) : <>
      {/* Candidate sub-tabs — Active / Hired / Rejected / Not Joined / Withdrawn */}
      <div className="flex gap-[6px] mb-3 flex-wrap whitespace-nowrap">
        {[["all", "All", candCounts.all], ["active", "Active", candCounts.active], ["hired", "Joined", candCounts.hired], ["rejected", "Rejected", candCounts.rejected], ["notjoined", "Not Joined", candCounts.notjoined], ["withdrawn", "Withdrawn", candCounts.withdrawn]].map(([k, l, n]) => (
          <button key={k} onClick={() => setCandBucket(k)} className={`px-3 py-[6px] rounded-lg text-[13px] font-bold flex items-center gap-[6px] cursor-pointer border transition-colors ${candBucket === k ? "bg-pg border-pb text-p" : "bg-s border-b0 text-t2 hover:text-t0"}`}>
            {l} <span className={`px-[6px] py-[1px] rounded-full text-[10px] font-extrabold ${candBucket === k ? "bg-p text-white" : "bg-b1 text-t2"}`}>{n}</span>
          </button>
        ))}
      </div>

      {/* Date filter — terminal buckets (Joined/Rejected/Not Joined) always
           show it, filtered on when the candidate landed there (updated_at).
           Active is unfiltered by default (shows everyone); picking a date
           field below turns a Period filter on for it too — useful for
           "who got offered this week" or "who's rescheduled to join in
           October" without waiting for them to reach a terminal bucket. */}
      {(candBucket === "active" || candBucket === "all") && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-[14px] text-t3 uppercase">Filter by date</span>
          <select
            value={candDateField}
            onChange={e => setCandDateField(e.target.value)}
            className="px-2 py-1 bg-bg1 border border-b0 rounded text-[14px] outline-none"
          >
            <option value="">No date filter</option>
            <option value="offer_initiated_date">Offer Initiated</option>
            <option value="offer_date">Offered</option>
            <option value="expected_join_date">Reschedule (Expected Join)</option>
            <option value="updated_at">Last Updated</option>
          </select>
          {candDateField && <>
            <input type="date" value={candFrom} onChange={e => setCandFrom(e.target.value)} className="px-2 py-1 bg-bg1 border border-b0 rounded text-[14px] outline-none" />
            <span className="text-[14px] text-t3">to</span>
            <input type="date" value={candTo} onChange={e => setCandTo(e.target.value)} className="px-2 py-1 bg-bg1 border border-b0 rounded text-[14px] outline-none" />
            <button onClick={() => { setCandFrom(ninetyDaysAgo); setCandTo(today); }} className="text-[14px] text-p font-semibold bg-transparent border-0 cursor-pointer">Reset to last 90 days</button>
            <button onClick={() => setCandDateField("")} className="text-[14px] text-t3 font-semibold bg-transparent border-0 cursor-pointer">Clear</button>
          </>}
        </div>
      )}
      {candBucket !== "active" && candBucket !== "all" && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-[14px] text-t3 uppercase">Period</span>
          <input type="date" value={candFrom} onChange={e => setCandFrom(e.target.value)} className="px-2 py-1 bg-bg1 border border-b0 rounded text-[14px] outline-none" />
          <span className="text-[14px] text-t3">to</span>
          <input type="date" value={candTo} onChange={e => setCandTo(e.target.value)} className="px-2 py-1 bg-bg1 border border-b0 rounded text-[14px] outline-none" />
          <button onClick={() => { setCandFrom(ninetyDaysAgo); setCandTo(today); }} className="text-[14px] text-p font-semibold bg-transparent border-0 cursor-pointer">Reset to last 90 days</button>
        </div>
      )}

      {/* Position + Stage filters — narrow the current bucket client-side.
           Position options come from what's actually in the loaded rows so
           HR isn't picking from a stale role list. Stage stays on the enum
           (skipping "Hired" on Active since that bucket only holds pre-
           hire stages). */}
      {(() => {
        const positions = [...new Set((candRows || []).map(r => r.pos).filter(Boolean))].sort();
        const stageOptions = candBucket === "active" ? [...STAGES.filter(s => s !== "Hired"), "On Hold"] : candBucket === "all" ? [...STAGES, "On Hold", "Withdrawn"] : STAGES;
        const recruiters = [...new Set((candRows || []).map(r => r.recruiterName).filter(Boolean))].sort();
        const sq = candSearch.trim().toLowerCase();
        const filteredRows = (candRows || []).filter(r =>
          (candPos === "all" || r.pos === candPos) &&
          (candStage === "all" || r.stg === candStage) &&
          (candRecruiter === "all" || r.recruiterName === candRecruiter) &&
          (!sq || `${r.nm || ""} ${r.pos || ""} ${r.recruiterName || ""}`.toLowerCase().includes(sq))
        );
        const hasFilter = candPos !== "all" || candStage !== "all" || candRecruiter !== "all" || !!sq;
        return <>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div style={{ width: 190 }}><Sl options={[{ v: "all", l: `All positions (${positions.length})` }, ...positions.map(p => ({ v: p, l: p }))]} value={candPos} onChange={setCandPos} /></div>
            <div style={{ width: 170 }}><Sl options={[{ v: "all", l: "All stages" }, ...stageOptions.map(s => ({ v: s, l: stageLbl(s) }))]} value={candStage} onChange={setCandStage} /></div>
            {/* Who's handling each candidate — lets HR Head see one
                 recruiter's whole pipeline (interviews, offers, rejections)
                 at a glance, combined with the date filter above. */}
            <div style={{ width: 180 }}><Sl options={[{ v: "all", l: `All HR / Recruiters (${recruiters.length})` }, ...recruiters.map(rn => ({ v: rn, l: rn }))]} value={candRecruiter} onChange={setCandRecruiter} /></div>
            <div style={{ width: 220 }}><Inp icon={Search} placeholder="Quick candidate search…" value={candSearch} onChange={setCandSearch} /></div>
            {hasFilter && (
              <>
                <span className="text-[14px] text-t3">Showing <b className="text-t0">{filteredRows.length}</b> of {(candRows || []).length}</span>
                <button onClick={() => { setCandPos("all"); setCandStage("all"); setCandRecruiter("all"); setCandSearch(""); }} className="text-[14px] text-p font-semibold bg-transparent border-0 cursor-pointer">Clear filters</button>
              </>
            )}
          </div>

          {/* Stage funnel — only on Active (the 4-stage pipeline). Reflects
               the current position filter so numbers stay coherent when HR
               narrows to a specific role. */}
          {candBucket === "active" && (() => {
            const pipelineStages = STAGES.filter(s => s !== "Hired");
            const posFiltered = candRows.filter(r =>
              (candPos === "all" || r.pos === candPos) &&
              (candRecruiter === "all" || r.recruiterName === candRecruiter)
            );
            const stageCounts = pipelineStages.map((s, i) => ({ s, i, n: posFiltered.filter(c => c.stg === s).length }));
            const total = stageCounts.reduce((sum, x) => sum + x.n, 0);
            return (
              <Cd style={{ marginBottom: 12 }}>
                <div className="flex items-center justify-between mb-[10px]">
                  <div className="flex items-baseline gap-[8px]">
                    <span className="text-[22px] font-extrabold text-t0">{total}</span>
                    <span className="text-[12px] font-bold uppercase tracking-wide text-t3">Active Pipeline</span>
                  </div>
                </div>
                {total > 0 && (
                  <div className="flex h-[8px] rounded-full overflow-hidden mb-[10px]" style={{ background: K.bg2 }}>
                    {stageCounts.filter(x => x.n > 0).map(x => (
                      <div key={x.s} style={{ width: `${x.n / total * 100}%`, background: STAGE_COLORS[x.i] }} />
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-x-[18px] gap-y-[4px]">
                  {stageCounts.map(x => (
                    <div key={x.s} className="flex items-center gap-[6px] text-[13px]">
                      <span className="w-[8px] h-[8px] rounded-full flex-shrink-0" style={{ background: STAGE_COLORS[x.i] }} />
                      <span className="font-bold text-t0">{x.n}</span>
                      <span className="text-t3">{stageLbl(x.s)}</span>
                    </div>
                  ))}
                </div>
              </Cd>
            );
          })()}

          {candLoading ? <div className="text-center p-[30px] text-t3 text-[14px]">Loading…</div> : <>
            <Tbl cols={[
          { k: "nm",  l: "Candidate",    r: r => <div className="flex items-center gap-[5px]"><Av nm={r.nm} sz={22} /><div><div className="text-[15px] font-bold text-t0 flex items-center gap-[6px]">{r.nm}{r.reopenCount > 0 && <span title={`Reapplied ${r.reopenCount} time${r.reopenCount === 1 ? "" : "s"}`} className="text-[10px] font-bold px-[7px] py-[1px] rounded-full" style={{ background: K.pg, color: K.p }}>Revisited</span>}</div>{r.email && <div className="text-[12px] text-t3">{r.email}</div>}</div></div> },
          { k: "pos", l: "Position",     r: r => <Bg c={K.p} sm>{r.pos}</Bg> },
          { k: "exp", l: "Exp",          r: r => <span className="text-[14px]">{r.exp ? r.exp + "yr" : "—"}</span> },
          { k: "ctc", l: "Expected CTC", r: r => <span className="text-[15px] font-bold text-t0">{r.expCtc ? fmtMoney(r.expCtc) : "—"}</span> },
          { k: "src", l: "Source",       r: r => <span className="text-[12px] text-t2">{r.src || "—"}</span> },
          { k: "recruiter", l: "HR / Recruiter", r: r => <span className="text-[12px] text-t2">{r.recruiterName || "—"}</span> },
          { k: "rat", l: "Rating",       r: r => r.rat ? <span className="flex items-center gap-[2px]"><Star size={10} color={K.a} fill={K.a} /><span className="font-semibold text-[14px]">{r.rat}</span></span> : <span className="text-t3">—</span> },
          { k: "stg", l: "Stage",        r: r => <Bg c={STAGE_COLORS[STAGES.indexOf(r.stg)] || (r.stg === "Rejected" ? K.r : r.stg === "Not Joined" ? K.a : r.stg === "Withdrawn" ? K.pu : r.stg === "On Hold" ? K.a : K.t3)} sm>{stageLbl(r.stg)}</Bg> },
          { k: "upd", l: "Last Updated", r: r => <span className="text-[12px] text-t2 font-mono">{r.updatedAt ? fmtDMY(r.updatedAt) : "—"}</span> },
          { k: "extra", l: candBucket === "hired" ? "Joined / Onboarding" : candBucket === "rejected" ? "Reason" : candBucket === "notjoined" ? "Exit" : candBucket === "withdrawn" ? "Reason" : "",
            r: r => candBucket === "hired"
              ? <span className="text-[12px] text-t2">{r.joinDate || "—"}{r.onboardingId ? ` · ${r.onboardingId}` : ""}</span>
              : candBucket === "rejected"
                ? (r.rejReason
                    ? <span className="text-[12px] text-r truncate max-w-[180px]" title={r.rejReason}>{r.rejReason}</span>
                    : <span className="text-t3">—</span>)
                : candBucket === "notjoined"
                  ? <span className="text-[12px] text-a font-semibold">Offer accepted, didn't join</span>
                  : candBucket === "withdrawn"
                    ? (r.rejReason
                        ? <span className="text-[12px] text-pu truncate max-w-[180px]" title={r.rejReason}>{r.rejReason}</span>
                        : <span className="text-t3">—</span>)
                    : <span className="text-t3">—</span>
          },
          { k: "act", l: "Actions",      r: r => <Bt v="ghost" s="xs" icon={Eye} onClick={() => setOpenCand(r.id)}>View</Bt> },
        ]} data={filteredRows} />
        {filteredRows.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">
          {hasFilter
            ? "No candidates match those filters."
            : candBucket === "all" ? "No candidates yet" : candBucket === "active" ? "No active candidates" :
              candBucket === "hired" ? "No joined candidates in this period" :
              candBucket === "rejected" ? "No rejected candidates in this period" :
              candBucket === "notjoined" ? "No not-joined candidates in this period" :
              "No withdrawn candidates in this period"}
        </div>}
      </>}
        </>;
      })()}

      {openCand && <CandidateDetail candId={openCand} onClose={() => setOpenCand(null)} onRefresh={() => { loadCands(); onRefresh?.(); }} canEdit={canEdit} canManagePostSelection={canManagePostSelection} canDelete={can('recruitment', 'candidates', 'delete_candidate', 'delete')} employees={employees} offices={offices} />}

      {showForm === "candidate" && (
        <AddCandidateModal roles={roles} offices={offices} employees={employees} myProfile={myProfile} onClose={() => setShowForm(null)} onCreated={() => { setShowForm(null); loadCands(); onRefresh?.(); }} onViewExisting={(id) => { setShowForm(null); setOpenCand(id); }} />
      )}
      </>}
    </div>
    {showPool && <RecruiterPoolModal employees={employees} ids={recruiterPool} onClose={() => setShowPool(false)} onSaved={setRecruiterPool} />}
    </RecruiterPoolCtx.Provider>
  );
};
